import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useTransition } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Divider,
  IconButton,
  InputNumber,
  Message,
  Panel,
  SelectPicker,
  Stack,
  Table,
  Tooltip,
  Whisper,
} from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';

import {
  useActivityHourForecastMonthsWithHours,
  useActivityHourForecast,
  useActivityHourForecastTotals,
  useTaskActivitiesHourForecast,
  useTaskActivitiesHourForecastTotals,
  useCloseDates,
  useUsersTimeOffDays,
  useProjectActivityForecastSummary,
  useProjects,
  useTask,
  useTaskOrders,
  useBulkUpsertActivityHourForecast,
  useAddActivitySupporter,
  useSetActivitySupporters,
  useUsers,
  useActivityHourForecastBlocks,
  usePrefetchActivityHourForecastBlocks,
} from '../hooks/useQueries';
import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';
import API_CONFIG from '../config/apiConfig';
import { exportSheetsToXlsx } from '../utils/excelExport';
import { buildMonthRangeInclusive } from '../utils/monthRange';
import usePermissions from '../hooks/usePermissions';

import {
  FlashFillButton,
  FlashFillGhost,
  buildFlashFillCellRuns,
  describeFlashFill,
  getFlashFillRunKey,
  isFlashFillShortcut,
  uniqueFlashFillRuns,
  useFlashFillUndo,
} from '../components/FlashFill';
import './ActivityHourForecastPage.css';
import './ActualsPage.css';

const { Column, HeaderCell, Cell } = Table;

const pad2 = (n) => String(n).padStart(2, '0');

// Use a local date string (YYYY-MM-DD) so month boundaries don't shift for users in timezones ahead of UTC.
const toLocalDateString = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  const yyyy = date.getFullYear();
  const mm = pad2(date.getMonth() + 1);
  const dd = pad2(date.getDate());
  return `${yyyy}-${mm}-${dd}`;
};

const parseLocalDateString = (yyyyMmDd) => {
  if (!yyyyMmDd || typeof yyyyMmDd !== 'string') return null;
  const m = yyyyMmDd.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
  if (!m) return null;
  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  const day = Number(m[3]);
  return new Date(year, monthIndex, day);
};

const formatMonthLabel = (monthStartIso) => {
  const d = parseLocalDateString(String(monthStartIso || '').slice(0, 10));
  if (!d) return '';
  const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = MONTHS_SHORT[d.getMonth()] || '';
  const yy = String(d.getFullYear()).slice(-2);
  return `${month} '${yy}`;
};

const getBaseWorkingHoursInMonth = (monthStartIso, hoursPerWorkingDay = 8) => {
  const d = parseLocalDateString(String(monthStartIso || '').slice(0, 10));
  if (!d) return null;

  const year = d.getFullYear();
  const monthIndex = d.getMonth();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  let workingDays = 0;
  for (let day = 1; day <= daysInMonth; day += 1) {
    const dow = new Date(year, monthIndex, day).getDay();
    // 0=Sun, 6=Sat
    if (dow >= 1 && dow <= 5) workingDays += 1;
  }

  return workingDays * Number(hoursPerWorkingDay);
};

const parseMonthDayFromIso10 = (yyyyMmDd) => {
  const s = String(yyyyMmDd || '').slice(0, 10);
  const m = s.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
  if (!m) return null;
  const monthIndex = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (!Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex > 11) return null;
  if (!Number.isInteger(day) || day < 1 || day > 31) return null;
  return { monthIndex, day };
};

const formatMonthDayLabel = (monthIndex, day) => `${pad2(Number(monthIndex) + 1)}/${pad2(Number(day))}`;

// Close dates are stored as recurring month/day definitions, so they are matched against each
// rendered month by month/day. Weekend occurrences are skipped: the working-hours baseline is
// weekdays * 8, so a holiday landing on a Saturday never removed a working hour and must not be
// subtracted again. (MyWorkplanPage applies the same rule, so both pages agree.)
const resolveCloseDatesInMonth = (monthStartIso, closeDates) => {
  const d = parseLocalDateString(String(monthStartIso || '').slice(0, 10));
  if (!d) return [];

  const year = d.getFullYear();
  const monthIndex = d.getMonth();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  return (Array.isArray(closeDates) ? closeDates : [])
    .map((cd) => {
      const md = parseMonthDayFromIso10(String(cd?.closeDate ?? cd?.close_date ?? '').slice(0, 10));
      if (!md || md.monthIndex !== monthIndex || md.day > daysInMonth) return null;

      const dow = new Date(year, monthIndex, md.day).getDay();
      if (dow === 0 || dow === 6) return null;

      return {
        monthIndex,
        day: md.day,
        label: formatMonthDayLabel(monthIndex, md.day),
        description: String(cd?.description ?? '').trim(),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.day - b.day);
};

// Total working hours in the month: weekday hours less company close dates. This is the number
// printed in the month headers and the baseline every utilization percentage starts from.
const getWorkingHoursInMonth = (monthStartIso, closeDates, hoursPerWorkingDay = 8) => {
  const base = getBaseWorkingHoursInMonth(monthStartIso, hoursPerWorkingDay);
  if (!Number.isFinite(base)) return null;
  const closeCount = resolveCloseDatesInMonth(monthStartIso, closeDates).length;
  return Math.max(0, Number(base) - closeCount * Number(hoursPerWorkingDay));
};

const DEFAULT_TIME_OFF_HOURS_PER_DAY = 8;

const isWeekdayIsoDate = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ''))) return false;
  const day = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return day >= 1 && day <= 5;
};

// The time off endpoint answers with a canonical day -> hours map, falling back to a list of
// entries on older payloads. Both shapes are normalized to { 'YYYY-MM-DD': hours }. The server
// already drops weekends, but the rule is re-applied rather than trusted: the working-hours
// baseline only ever counted weekdays, so a Saturday must not be subtracted from it.
const buildTimeOffHoursByDay = (payload) => {
  const byDay = {};

  const addDay = (rawDay, rawHours) => {
    const day = String(rawDay || '').slice(0, 10);
    if (!isWeekdayIsoDate(day)) return;
    const hours = Number(rawHours);
    byDay[day] = Number.isFinite(hours) && hours >= 0 ? hours : DEFAULT_TIME_OFF_HOURS_PER_DAY;
  };

  const hasByDay =
    payload &&
    typeof payload === 'object' &&
    payload.time_off_hours_by_day &&
    typeof payload.time_off_hours_by_day === 'object';

  if (hasByDay) {
    for (const [rawDay, rawHours] of Object.entries(payload.time_off_hours_by_day)) addDay(rawDay, rawHours);
    return byDay;
  }

  if (Array.isArray(payload?.entries)) {
    for (const entry of payload.entries) addDay(entry?.date ?? entry?.time_off_date, entry?.hours);
  }

  return byDay;
};

const sumTimeOffHoursInMonth = (hoursByDay, monthStartIso) => {
  if (!hoursByDay || typeof hoursByDay !== 'object') return 0;

  const monthKey = String(monthStartIso || '').slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(monthKey)) return 0;

  let total = 0;
  for (const [day, rawHours] of Object.entries(hoursByDay)) {
    if (String(day).slice(0, 7) !== monthKey) continue;
    const hours = Number(rawHours);
    if (!Number.isFinite(hours) || hours <= 0) continue;
    total += hours;
  }
  return total;
};

// The one utilization formula, shared by the main table and the View All sections so the same
// user can never read differently in the two places. Order of operations: the month's working
// hours, less time off, and only then scaled by the non-reserved share -- reserved hours are a
// share of the time the user is actually at work, so that reduction has to come last.
const getMonthCapacity = (workingHours, timeOffHours, fteScale) => {
  if (!Number.isFinite(workingHours)) return null;
  const afterTimeOff = Math.max(0, Number(workingHours) - (Number.isFinite(timeOffHours) ? timeOffHours : 0));
  const scale = Number.isFinite(fteScale) ? Math.max(0, fteScale) : 1;
  return afterTimeOff * scale;
};

const getUtilizationPercent = (committedHours, monthCapacity) => {
  if (!Number.isFinite(monthCapacity) || monthCapacity <= 0) return null;
  const committed = Number(committedHours);
  if (!Number.isFinite(committed)) return null;
  return Math.round((Math.max(0, committed) / monthCapacity) * 100);
};

// One batched fetch of time off for every user in a table, keyed to the months on screen.
// Shares react-query's ['userTimeOffDays', userId] cache with the rest of the app, so the main
// table and the View All sections below it never refetch the same user twice.
const useTimeOffHoursByUserMonth = (userIds, monthStarts) => {
  const queries = useUsersTimeOffDays(userIds);

  const timeOffHoursByUserMonth = useMemo(() => {
    const out = new Map();
    userIds.forEach((userId, idx) => {
      const byDay = buildTimeOffHoursByDay(queries?.[idx]?.data);
      for (const ms of monthStarts) out.set(`${userId}|${ms}`, sumTimeOffHoursInMonth(byDay, ms));
    });
    return out;
  }, [monthStarts, queries, userIds]);

  const timeOffError = useMemo(() => queries.find((q) => q?.error)?.error || null, [queries]);

  return { timeOffHoursByUserMonth, timeOffError };
};

const collectRowUserIds = (rows) => {
  const ids = new Set();
  for (const row of Array.isArray(rows) ? rows : []) {
    const id = Number(row?.userId);
    if (Number.isInteger(id) && id > 0) ids.add(id);
  }
  return Array.from(ids.values());
};

const formatRate = (rate) => {
  if (rate === null || rate === undefined || rate === '') return '—';
  const n = Number(rate);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const formatCost = (rate, hours) => {
  if (rate === null || rate === undefined || rate === '') return '—';
  const r = Number(rate);
  const h = Number(hours);
  if (!Number.isFinite(r) || !Number.isFinite(h)) return '—';
  return (r * h).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const buildRateSegments = (monthStarts, getRateForMonth) => {
  const segments = [];
  let prevKey = null;
  for (const ms of monthStarts) {
    const rate = getRateForMonth(ms);
    const key = rate === null || rate === undefined ? 'r:null' : Number.isFinite(Number(rate)) ? `r:${Number(rate)}` : 'r:null';
    const last = segments[segments.length - 1];

    if (!last || prevKey !== key) {
      segments.push({ start: ms, end: ms, rate });
      prevKey = key;
    } else {
      last.end = ms;
    }
  }
  return segments;
};

const buildRateSegmentsWithEffectiveDate = (monthStarts, getRateForMonth, getEffectiveDateForMonth) => {
  const segments = [];
  let prevKey = null;
  for (const ms of monthStarts) {
    const rate = getRateForMonth(ms);
    const eff = String(getEffectiveDateForMonth(ms) ?? '').slice(0, 10);
    const keyRate = rate === null || rate === undefined ? 'r:null' : Number.isFinite(Number(rate)) ? `r:${Number(rate)}` : 'r:null';
    const keyEff = /^\d{4}-\d{2}-\d{2}$/.test(eff) ? `e:${eff}` : 'e:null';
    const key = `${keyRate}|${keyEff}`;
    const last = segments[segments.length - 1];

    if (!last || prevKey !== key) {
      segments.push({ rate, effectiveDate: /^\d{4}-\d{2}-\d{2}$/.test(eff) ? eff : null });
      prevKey = key;
    }
  }
  return segments;
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '—';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const calcWindowCostForRows = (rows, monthStarts) => {
  const list = Array.isArray(rows) ? rows : [];
  const months = Array.isArray(monthStarts) ? monthStarts : [];
  let total = 0;

  for (const row of list) {
    for (const ms of months) {
      const hours = Number(row?.hours?.[ms] ?? 0);
      const rate = Number(row?.rates?.[ms] ?? row?.rate);
      if (!Number.isFinite(hours) || !Number.isFinite(rate)) continue;
      total += hours * rate;
    }
  }

  return total;
};

const snapToQuarter = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round(v / 0.25) * 0.25;
};

const truncateText = (value, maxLen = 70) => {
  const s = String(value ?? '');
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
};

// When an activity's forecast hours were last saved (the newest change to any of its cells, any
// month). The date shows; the exact time is in the hover. Renders nothing until the forecast loads.
function ForecastLastSaved({ forecast, parenthesized = false }) {
  if (!forecast) return null;
  const at = forecast.lastSavedAt ? new Date(forecast.lastSavedAt) : null;
  const valid = at && !Number.isNaN(at.getTime());
  const text = valid
    ? `Last saved ${at.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
    : 'Not saved yet';
  return (
    <span
      className="ppc-forecast-last-saved"
      title={valid ? `Last saved ${at.toLocaleString()}` : 'No forecast hours have been saved for this activity yet.'}
    >
      {parenthesized ? `(${text})` : text}
    </span>
  );
}

const WINDOW_SIZE = 6;
// How long the month columns take to slide one column over when stepping the window by a month.
const MONTH_SLIDE_MS = 160;
// How many months either side of the window stay loaded, so stepping through them is instant.
const MONTH_PREFETCH_RANGE = 6;
// Month stepping updates the URL's start only once clicking pauses for this long.
const URL_START_DEBOUNCE_MS = 400;
const PAST_FORECAST_OVERRIDE_CLICK_THRESHOLD = 5;

const isIsoDate = (s) => typeof s === 'string' && /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(s);

const resolveApiBaseUrl = () => {
  const raw = String(API_CONFIG?.BASE_URL || '').trim();
  if (!raw) return '/api';

  const withoutTrailing = raw.replace(/\/+$/, '');
  if (/\/api$/i.test(withoutTrailing)) return withoutTrailing;
  return `${withoutTrailing}/api`;
};

const getCurrentYearStartMonthIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-01-01`;
};

const toMonthStartIso = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-01`;
};

const addMonths = (monthStartIso, deltaMonths) => {
  const d = parseLocalDateString(String(monthStartIso || '').slice(0, 10));
  if (!d) return null;
  const out = new Date(d.getFullYear(), d.getMonth() + Number(deltaMonths || 0), 1);
  return toLocalDateString(out);
};

const readStartFromSearchParams = (searchParams) => {
  const raw = String(searchParams?.get?.('start') || '').slice(0, 10);
  const parsed = parseLocalDateString(raw);
  if (!parsed) return null;
  return toMonthStartIso(parsed);
};

const normalizeMonthStart = (yyyyMmDd) => {
  const raw = String(yyyyMmDd || '').slice(0, 10);
  const parsed = parseLocalDateString(raw);
  if (!parsed) return null;
  return `${raw.slice(0, 7)}-01`;
};

const clampMonthToRange = (monthStartIso, minMonthIso, maxMonthIso) => {
  const ms = normalizeMonthStart(monthStartIso);
  if (!ms) return null;

  const min = normalizeMonthStart(minMonthIso);
  const max = normalizeMonthStart(maxMonthIso);

  if (min && ms < min) return min;
  if (max && ms > max) return max;
  return ms;
};

const filterMonthsToRange = (months, minMonthIso, maxMonthIso) => {
  const list = Array.isArray(months) ? months : [];
  const min = normalizeMonthStart(minMonthIso);
  const max = normalizeMonthStart(maxMonthIso);

  return list
    .map((m) => normalizeMonthStart(m))
    .filter(Boolean)
    .filter((m) => {
      if (min && m < min) return false;
      if (max && m > max) return false;
      return true;
    });
};

// Index into the sorted months-with-hours list where the window shown for `start` begins: the first
// month on or after `start`, pulled back so the window stays full at the end of the list.
const getSparseWindowStartIdx = (monthsWithHours, start) => {
  const maxStartIdx = Math.max(0, monthsWithHours.length - WINDOW_SIZE);
  const normalizedStart = normalizeMonthStart(start);
  const idx = normalizedStart ? monthsWithHours.findIndex((m) => m >= normalizedStart) : 0;
  return idx === -1 ? maxStartIdx : Math.min(maxStartIdx, idx);
};

// Applies flash fill runs to the ref-held drafts and records how to revert them for Undo / Ctrl+Z.
function useFlashFillDrafts({ draftByKeyRef, draftInputByKeyRef, setDraftVersion, disabled }) {
  const { recordFlashFill, clearFlashFillUndo } = useFlashFillUndo({ disabled });

  const applyFlashFill = (row, runs) => {
    if (disabled || runs.length === 0) return;

    const changes = [];
    for (const run of runs) {
      for (const ms of run.months) {
        const key = `${row.userId}|${ms}`;
        const before = {
          hadDraft: key in draftByKeyRef.current,
          draft: draftByKeyRef.current[key],
          hadInput: key in draftInputByKeyRef.current,
          input: draftInputByKeyRef.current[key],
        };

        const current = Number(row?.hours?.[ms] ?? 0);
        if (run.value === current) {
          delete draftByKeyRef.current[key];
          delete draftInputByKeyRef.current[key];
        } else {
          draftByKeyRef.current[key] = run.value;
          draftInputByKeyRef.current[key] = run.value;
        }
        changes.push({ key, before, after: draftByKeyRef.current[key] });
      }
    }
    setDraftVersion((x) => x + 1);

    recordFlashFill({
      message: describeFlashFill(row?.userName, runs),
      undo: () => {
        for (const { key, before, after } of changes) {
          // Leave cells the user has edited since the fill.
          if (draftByKeyRef.current[key] !== after) continue;
          if (before.hadDraft) draftByKeyRef.current[key] = before.draft;
          else delete draftByKeyRef.current[key];
          if (before.hadInput) draftInputByKeyRef.current[key] = before.input;
          else delete draftInputByKeyRef.current[key];
        }
        setDraftVersion((x) => x + 1);
      },
    });
  };

  return { applyFlashFill, clearFlashFillUndo };
}

function ViewAllActivityForecastSection({
  taskId,
  activityId,
  activityName,
  activityNumber,
  start,
  monthStarts,
  userFteById,
  isPastMonthLocked,
  onLockedPastMonthAttempt,
  onTotalsChange,
  onUnsavedChange,
  prefetchedForecast,
  prefetchedAllTimeTotals,
  prefetchedLoading,
  usePrefetchedData,
  canForecast,
}) {
  // Shared with the main table through react-query's cache, so both measure % util against the
  // same close-date-adjusted working hours.
  const { data: closeDatesRes } = useCloseDates();
  const workingHoursByMonthStart = useMemo(() => {
    const list = Array.isArray(closeDatesRes) ? closeDatesRes : [];
    const out = new Map();
    for (const ms of monthStarts) out.set(ms, getWorkingHoursInMonth(ms, list));
    return out;
  }, [closeDatesRes, monthStarts]);

  const {
    data: fetchedForecast,
    isLoading: fetchedForecastLoading,
    error: fetchedForecastError,
  } = useActivityHourForecast(
    Number.isInteger(taskId) ? taskId : null,
    Number.isInteger(activityId) ? activityId : null,
    String(start || '').slice(0, 10),
    WINDOW_SIZE,
    { enabled: !usePrefetchedData && Number.isInteger(taskId) && Number.isInteger(activityId) }
  );

  const { data: fetchedAllTimeTotals } = useActivityHourForecastTotals(
    Number.isInteger(taskId) ? taskId : null,
    Number.isInteger(activityId) ? activityId : null,
    { enabled: !usePrefetchedData && Number.isInteger(taskId) && Number.isInteger(activityId) }
  );

  const forecast = usePrefetchedData ? prefetchedForecast : fetchedForecast;
  const forecastLoading = usePrefetchedData ? Boolean(prefetchedLoading) : fetchedForecastLoading;
  const forecastError = usePrefetchedData ? null : fetchedForecastError;
  const allTimeTotals = usePrefetchedData ? prefetchedAllTimeTotals : fetchedAllTimeTotals;

  const bulkUpsert = useBulkUpsertActivityHourForecast();
  const [savingAll, setSavingAll] = useState(false);

  const draftByKeyRef = useRef({});
  const draftInputByKeyRef = useRef({});
  const [draftVersion, setDraftVersion] = useState(0);

  const getDraftKey = (userId, monthStart) => `${userId}|${monthStart}`;

  const getRateForMonth = (row, monthStart) => {
    if (!row || !monthStart) return null;

    const ratesMap = row?.rates;
    const hasMonthRatesMap = Boolean(ratesMap && typeof ratesMap === 'object' && Object.keys(ratesMap).length > 0);

    if (hasMonthRatesMap) {
      const hasKey = Object.prototype.hasOwnProperty.call(ratesMap, monthStart);
      if (!hasKey) return null;
      const raw = ratesMap[monthStart];
      if (raw === null || raw === undefined || raw === '') return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }

    const fallback = Number(row?.rate);
    return Number.isFinite(fallback) ? fallback : null;
  };

  const getRateEffectiveDateForMonth = (row, monthStart) => {
    const s = String(row?.rateEffectiveDates?.[monthStart] ?? '').slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
  };

  const getEffectiveHours = (row, monthStart) => {
    const key = getDraftKey(row.userId, monthStart);
    const draft = draftByKeyRef.current[key];
    if (draft !== undefined) return draft;
    return Number(row?.hours?.[monthStart] ?? 0);
  };

  const getDisplayHours = (row, monthStart) => {
    const key = getDraftKey(row.userId, monthStart);
    if (key in draftInputByKeyRef.current) return draftInputByKeyRef.current[key];
    const draft = draftByKeyRef.current[key];
    if (draft !== undefined) return draft;
    return Number(row?.hours?.[monthStart] ?? 0);
  };

  const rows = useMemo(() => {
    const base = Array.isArray(forecast?.rows) ? forecast.rows : [];
    const normalized = base
      .filter((r) => Number.isInteger(Number(r?.userId)))
      .map((r) => ({
        userId: Number(r.userId),
        userName: r.userName || '—',
        role: r.role || 'support',
        rate: r.rate,
        rates: r.rates || {},
        rateEffectiveDates: r.rateEffectiveDates || r.rate_effective_dates || {},
        hours: r.hours || {},
        committedHours: r.committedHours || r.committed_hours || {},
      }));

    normalized.sort((a, b) => {
      const aLead = a.role === 'lead' ? 0 : 1;
      const bLead = b.role === 'lead' ? 0 : 1;
      if (aLead !== bLead) return aLead - bLead;
      return String(a.userName).localeCompare(String(b.userName));
    });

    const totals = {};
    const totalCosts = {};
    for (const ms of monthStarts) {
      const totalHours = normalized.reduce((sum, row) => sum + Number(getEffectiveHours(row, ms) ?? 0), 0);
      totals[ms] = totalHours;

      totalCosts[ms] = normalized.reduce((sum, row) => {
        const h = Number(getEffectiveHours(row, ms) ?? 0);
        const r = getRateForMonth(row, ms);
        if (!Number.isFinite(h) || !Number.isFinite(Number(r))) return sum;
        return sum + Number(r) * h;
      }, 0);
    }

    return [
      ...normalized,
      {
        userId: -1,
        userName: 'Total',
        role: 'total',
        rate: null,
        rates: {},
        hours: totals,
        costs: totalCosts,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecast?.rows, monthStarts, draftVersion]);

  const rowUserIds = useMemo(() => collectRowUserIds(rows), [rows]);
  const { timeOffHoursByUserMonth, timeOffError } = useTimeOffHoursByUserMonth(rowUserIds, monthStarts);

  const isTotalRow = (rowData) => Number(rowData?.userId) === -1 || rowData?.role === 'total';

  const commitCell = (row, monthStart) => {
    if (isTotalRow(row)) return;
    if (isPastMonthLocked(monthStart)) return;

    const key = getDraftKey(row.userId, monthStart);
    const raw = key in draftInputByKeyRef.current ? draftInputByKeyRef.current[key] : undefined;
    const parsed = raw === '' || raw === null || raw === undefined ? 0 : Number(raw);
    const normalized = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    const snapped = snapToQuarter(normalized);

    const current = Number(row?.hours?.[monthStart] ?? 0);
    if (snapped === current) {
      delete draftByKeyRef.current[key];
      delete draftInputByKeyRef.current[key];
    } else {
      draftByKeyRef.current[key] = snapped;
      draftInputByKeyRef.current[key] = snapped;
    }
    setDraftVersion((x) => x + 1);
  };

  const onChangeCell = (row, monthStart, value) => {
    if (isTotalRow(row)) return;
    if (isPastMonthLocked(monthStart)) return;

    const key = getDraftKey(row.userId, monthStart);
    draftInputByKeyRef.current[key] = value;

    const v = value === '' || value === null || value === undefined ? 0 : Number(value);
    const normalized = Number.isFinite(v) ? Math.max(0, v) : 0;
    const snapped = snapToQuarter(normalized);
    const current = Number(row?.hours?.[monthStart] ?? 0);

    if (snapped === current) delete draftByKeyRef.current[key];
    else draftByKeyRef.current[key] = snapped;

    setDraftVersion((x) => x + 1);
  };

  const flashFillRunsByUserId = useMemo(
    () => buildFlashFillCellRuns(rows.filter((r) => !isTotalRow(r)), monthStarts, getEffectiveHours, isPastMonthLocked),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, monthStarts, isPastMonthLocked]
  );

  const { applyFlashFill, clearFlashFillUndo } = useFlashFillDrafts({
    draftByKeyRef,
    draftInputByKeyRef,
    setDraftVersion,
    disabled: savingAll,
  });

  const dirtyEdits = useMemo(() => {
    const base = Array.isArray(forecast?.rows) ? forecast.rows : [];
    const dirty = [];
    const drafts = draftByKeyRef.current;

    for (const row of base) {
      const userId = Number(row?.userId);
      if (!Number.isInteger(userId)) continue;

      for (const ms of monthStarts) {
        if (isPastMonthLocked(ms)) continue;

        const key = getDraftKey(userId, ms);
        if (!(key in drafts)) continue;

        const next = Number(drafts[key] ?? 0);
        const current = Number(row?.hours?.[ms] ?? 0);
        if (next === current) continue;

        dirty.push({ userId, monthStart: ms, hours: Number.isFinite(next) ? next : 0 });
      }
    }

    return dirty;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecast?.rows, monthStarts, draftVersion, isPastMonthLocked]);

  const hasUnsavedChanges = dirtyEdits.length > 0;

  useEffect(() => {
    if (!Number.isInteger(activityId)) return undefined;
    if (typeof onUnsavedChange !== 'function') return undefined;
    if (!hasUnsavedChanges) return undefined;

    onUnsavedChange(activityId, true);

    return () => {
      onUnsavedChange(activityId, false);
    };
  }, [activityId, hasUnsavedChanges, onUnsavedChange]);

  const allTimeTotalHoursByUserId = useMemo(() => {
    const out = new Map();
    const list = Array.isArray(allTimeTotals?.rows) ? allTimeTotals.rows : [];
    for (const r of list) {
      const uid = Number(r?.userId);
      if (!Number.isInteger(uid)) continue;
      out.set(uid, Number(r?.totalHours ?? 0));
    }
    return out;
  }, [allTimeTotals?.rows]);

  const sectionDisplayedTotals = useMemo(() => {
    const totalRow = rows.find((r) => isTotalRow(r));
    if (!totalRow) return { displayedHours: 0, displayedCost: 0 };

    let displayedHours = 0;
    let displayedCost = 0;
    for (const ms of monthStarts) {
      displayedHours += Number(totalRow?.hours?.[ms] ?? 0);
      displayedCost += Number(totalRow?.costs?.[ms] ?? 0);
    }
    return { displayedHours, displayedCost };
  }, [monthStarts, rows]);

  const sectionFullTotals = useMemo(() => {
    const baseHours = Number(allTimeTotals?.totalHours ?? 0);
    const baseCost = Number(allTimeTotals?.totalCost ?? 0);

    const getEffectiveHoursForCell = (row, monthStart) => {
      const key = `${row?.userId}|${monthStart}`;
      const draft = draftByKeyRef.current[key];
      if (draft !== undefined) return Number(draft ?? 0);
      return Number(row?.hours?.[monthStart] ?? 0);
    };

    const getWindowServerHours = (row) => monthStarts.reduce((sum, ms) => sum + Number(row?.hours?.[ms] ?? 0), 0);
    const getWindowEffectiveHours = (row) => monthStarts.reduce((sum, ms) => sum + Number(getEffectiveHoursForCell(row, ms) ?? 0), 0);
    const getWindowServerCost = (row) =>
      monthStarts.reduce((sum, ms) => {
        const h = Number(row?.hours?.[ms] ?? 0);
        const rate = Number(getRateForMonth(row, ms) ?? 0);
        if (!Number.isFinite(h) || !Number.isFinite(rate)) return sum;
        return sum + h * rate;
      }, 0);
    const getWindowEffectiveCost = (row) =>
      monthStarts.reduce((sum, ms) => {
        const h = Number(getEffectiveHoursForCell(row, ms) ?? 0);
        const rate = Number(getRateForMonth(row, ms) ?? 0);
        if (!Number.isFinite(h) || !Number.isFinite(rate)) return sum;
        return sum + h * rate;
      }, 0);

    const deltaHours = rows
      .filter((r) => !isTotalRow(r))
      .reduce((sum, r) => sum + (getWindowEffectiveHours(r) - getWindowServerHours(r)), 0);

    const deltaCost = rows
      .filter((r) => !isTotalRow(r))
      .reduce((sum, r) => sum + (getWindowEffectiveCost(r) - getWindowServerCost(r)), 0);

    if (!allTimeTotals) {
      return {
        totalHours: Number(sectionDisplayedTotals.displayedHours ?? 0),
        totalCost: Number(sectionDisplayedTotals.displayedCost ?? 0),
      };
    }

    return {
      totalHours: baseHours + deltaHours,
      totalCost: baseCost + deltaCost,
    };
  }, [allTimeTotals, monthStarts, rows, sectionDisplayedTotals.displayedCost, sectionDisplayedTotals.displayedHours]);

  const [publishedSectionFullTotals, setPublishedSectionFullTotals] = useState(() => ({
    totalHours: Number(sectionFullTotals.totalHours ?? 0),
    totalCost: Number(sectionFullTotals.totalCost ?? 0),
  }));

  const [pinnedSavedFullTotals, setPinnedSavedFullTotals] = useState(null);

  useEffect(() => {
    if (hasUnsavedChanges || savingAll || pinnedSavedFullTotals) return;
    setPublishedSectionFullTotals({
      totalHours: Number(sectionFullTotals.totalHours ?? 0),
      totalCost: Number(sectionFullTotals.totalCost ?? 0),
    });
  }, [
    hasUnsavedChanges,
    savingAll,
    pinnedSavedFullTotals,
    sectionFullTotals.totalHours,
    sectionFullTotals.totalCost,
  ]);

  useEffect(() => {
    if (!pinnedSavedFullTotals) return;

    const serverHours = Number(allTimeTotals?.totalHours);
    const serverCost = Number(allTimeTotals?.totalCost);

    if (!Number.isFinite(serverHours) || !Number.isFinite(serverCost)) return;

    const hoursDiff = Math.abs(serverHours - Number(pinnedSavedFullTotals.totalHours ?? 0));
    const costDiff = Math.abs(serverCost - Number(pinnedSavedFullTotals.totalCost ?? 0));

    if (hoursDiff <= 0.01 && costDiff <= 0.01) {
      setPinnedSavedFullTotals(null);
    }
  }, [allTimeTotals?.totalHours, allTimeTotals?.totalCost, pinnedSavedFullTotals]);

  const stableSectionFullTotals = useMemo(() => {
    if (pinnedSavedFullTotals) {
      return {
        totalHours: Number(pinnedSavedFullTotals.totalHours ?? 0),
        totalCost: Number(pinnedSavedFullTotals.totalCost ?? 0),
      };
    }

    return {
      totalHours: Number(publishedSectionFullTotals.totalHours ?? 0),
      totalCost: Number(publishedSectionFullTotals.totalCost ?? 0),
    };
  }, [publishedSectionFullTotals.totalHours, publishedSectionFullTotals.totalCost, pinnedSavedFullTotals]);

  useEffect(() => {
    if (!Number.isInteger(activityId)) return undefined;
    if (typeof onTotalsChange !== 'function') return undefined;
    if (!hasUnsavedChanges && !savingAll && !pinnedSavedFullTotals) return undefined;
    if (forecastLoading) return undefined;

    onTotalsChange(activityId, {
      totalHours: Number(stableSectionFullTotals.totalHours ?? 0),
      totalCost: Number(stableSectionFullTotals.totalCost ?? 0),
    });

    return () => {
      onTotalsChange(activityId, null);
    };
  }, [
    activityId,
    forecastLoading,
    hasUnsavedChanges,
    onTotalsChange,
    pinnedSavedFullTotals,
    savingAll,
    stableSectionFullTotals.totalHours,
    stableSectionFullTotals.totalCost,
  ]);

  const saveAll = async () => {
    if (!hasUnsavedChanges) return;
    if (!Number.isInteger(taskId) || !Number.isInteger(activityId)) return;
    if (savingAll) return;

    setSavingAll(true);
    try {
      await bulkUpsert.mutateAsync({ taskId, activityId, edits: dirtyEdits });

      const justSavedFullTotals = {
        totalHours: Number(sectionFullTotals.totalHours ?? 0),
        totalCost: Number(sectionFullTotals.totalCost ?? 0),
      };

      // Publish full totals only on save and hold them until refetch catches up.
      setPublishedSectionFullTotals(justSavedFullTotals);
      setPinnedSavedFullTotals(justSavedFullTotals);

      for (const edit of dirtyEdits) {
        delete draftByKeyRef.current[getDraftKey(edit.userId, edit.monthStart)];
        delete draftInputByKeyRef.current[getDraftKey(edit.userId, edit.monthStart)];
      }
      clearFlashFillUndo();
      setDraftVersion((x) => x + 1);
    } finally {
      setSavingAll(false);
    }
  };

  const activityNumPrefix = Number.isInteger(activityNumber) ? `#${activityNumber} ` : '';

  return (
    <Panel
      bordered
      className="ppc-forecast-view-all-section"
      header={
        <div className="ppc-forecast-view-all-section__header">
          <div className="ppc-forecast-view-all-section__header-main">
            <div className="ppc-forecast-view-all-section__title">
              {activityNumPrefix}{activityName || '—'} <ForecastLastSaved forecast={forecast} parenthesized />
            </div>
          </div>
          {canForecast ? (
            <button
              type="button"
              className="ppc-actuals-btn ppc-forecast-btn--sm"
              disabled={!hasUnsavedChanges || savingAll}
              onClick={saveAll}
            >
              {savingAll ? 'Saving…' : 'Save'}
            </button>
          ) : null}
        </div>
      }
    >
      {forecastError ? (
        <Message type="error" showIcon style={{ marginBottom: 10 }}>
          Failed to load activity forecast.
        </Message>
      ) : null}

      {timeOffError ? (
        <Message type="warning" showIcon style={{ marginBottom: 10 }}>
          % util is not counting time off (failed to load time off entries).
        </Message>
      ) : null}

      <div className="ppc-forecast-view-all-table-wrap">
      <Table
        className="ppc-forecast-view-all-table"
        data={rows}
        autoHeight
        bordered
        cellBordered
        loading={forecastLoading}
        rowKey={(row) => `${row.userId}`}
        rowClassName={(rowData) => (isTotalRow(rowData) ? 'ppc-forecast-view-all-total-row' : '')}
        rowHeight={66}
        headerHeight={38}
        style={{ width: '100%' }}
      >
        <Column width={170} fixed>
          <HeaderCell>Person</HeaderCell>
          <Cell>
            {(rowData) => {
              if (isTotalRow(rowData)) return <span style={{ fontWeight: 800 }}>Total</span>;
              const role = rowData?.role === 'lead' ? 'Lead' : 'Support';
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', textAlign: 'center' }}>
                  <div style={{ fontWeight: 700 }}>{rowData?.userName || '—'}</div>
                  <div style={{ fontSize: 11, color: '#777' }}>{role}</div>
                </div>
              );
            }}
          </Cell>
        </Column>

        <Column width={140} fixed>
          <HeaderCell>Rate</HeaderCell>
          <Cell>
            {(rowData) => {
              if (isTotalRow(rowData)) return <span style={{ fontWeight: 800 }}>—</span>;

              const segments = buildRateSegmentsWithEffectiveDate(
                monthStarts,
                (ms) => getRateForMonth(rowData, ms),
                (ms) => getRateEffectiveDateForMonth(rowData, ms)
              );
              const displaySegments = segments.slice(0, 3);

              return (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: 2,
                    height: '100%',
                  }}
                >
                  {displaySegments.map((seg) => (
                    <div key={`${String(seg.effectiveDate)}-${String(seg.rate)}`} style={{ lineHeight: '16px' }}>
                      <div style={{ fontWeight: 700 }}>{formatRate(seg.rate)}</div>
                      <div style={{ fontSize: 11, color: '#777' }}>Eff: {seg.effectiveDate || '—'}</div>
                    </div>
                  ))}
                  {segments.length > displaySegments.length ? (
                    <div style={{ fontSize: 11, color: '#777' }}>…</div>
                  ) : null}
                </div>
              );
            }}
          </Cell>
        </Column>

        {monthStarts.map((ms) => (
          <Column key={`${activityId}-${ms}`} width={170} flexGrow={1} align="right">
            <HeaderCell>{formatMonthLabel(ms)}</HeaderCell>
            <Cell>
              {(rowData) => {
                const isTotal = isTotalRow(rowData);
                if (isTotal) {
                  const totalHours = Number(rowData?.hours?.[ms] ?? 0);
                  const totalCost = Number(rowData?.costs?.[ms] ?? 0);
                  return (
                    <div
                      style={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-end',
                        gap: 2,
                      }}
                    >
                      <div style={{ fontWeight: 800 }}>{totalHours.toFixed(2)}h</div>
                      <div style={{ fontWeight: 800, fontSize: 12 }}>{formatCurrency(totalCost)}</div>
                    </div>
                  );
                }

                const value = getDisplayHours(rowData, ms);
                const monthLocked = isPastMonthLocked(ms);
                const cost = formatCost(getRateForMonth(rowData, ms), value);
                const flashFillRun = flashFillRunsByUserId.get(rowData?.userId)?.get(ms);
                const flashFillRunKey = flashFillRun ? getFlashFillRunKey(rowData.userId, flashFillRun) : undefined;

                const workingHours = workingHoursByMonthStart.get(ms);
                const ftePercent = Number(userFteById.get(Number(rowData?.userId)) ?? 100);
                const fteScale = Number.isFinite(ftePercent) ? Math.max(0, ftePercent) / 100 : 1;
                const timeOffHours = Number(timeOffHoursByUserMonth.get(`${rowData?.userId}|${ms}`) ?? 0);
                const monthCapacity = getMonthCapacity(workingHours, timeOffHours, fteScale);
                const committedPct = getUtilizationPercent(rowData?.committedHours?.[ms] ?? 0, monthCapacity);
                const committedLabel = Number.isInteger(committedPct) ? `${committedPct}% util` : null;

                return (
                  <div
                    data-flash-fill-run={flashFillRunKey}
                    style={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'flex-end',
                      gap: 2,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                      {flashFillRun && flashFillRun.months[0] === ms ? (
                        canForecast ? (
                          <FlashFillButton
                            runKey={flashFillRunKey}
                            run={flashFillRun}
                            disabled={savingAll}
                            onAccept={() => applyFlashFill(rowData, [flashFillRun])}
                          />
                        ) : null
                      ) : null}
                      {(() => {
                        const inputNode = (
                          <InputNumber
                            size="sm"
                            min={0}
                            step={0.25}
                            precision={2}
                            value={value}
                            onFocus={(e) => {
                              queueMicrotask(() => e?.target?.select?.());
                            }}
                            onClick={(e) => {
                              queueMicrotask(() => e?.target?.select?.());
                            }}
                            onChange={(v) => onChangeCell(rowData, ms, v)}
                            onBlur={() => commitCell(rowData, ms)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                commitCell(rowData, ms);
                              } else if (isFlashFillShortcut(e)) {
                                e.preventDefault();
                                applyFlashFill(rowData, uniqueFlashFillRuns(flashFillRunsByUserId.get(rowData?.userId)));
                              }
                            }}
                            disabled={savingAll || monthLocked || !canForecast}
                            style={{ width: 120 }}
                          />
                        );

                        if (!monthLocked) {
                          if (!flashFillRun) return inputNode;
                          return <FlashFillGhost text={flashFillRun.value.toFixed(2)}>{inputNode}</FlashFillGhost>;
                        }

                        return (
                          <Whisper trigger="hover" placement="top" speaker={<Tooltip>Cannot edit past forecasts</Tooltip>}>
                            <span style={{ position: 'relative', display: 'inline-flex', width: 120 }}>
                              {inputNode}
                              <button
                                type="button"
                                aria-label="Cannot edit past forecasts"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (typeof onLockedPastMonthAttempt === 'function') onLockedPastMonthAttempt();
                                }}
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  border: 'none',
                                  background: 'transparent',
                                  padding: 0,
                                  margin: 0,
                                  cursor: 'not-allowed',
                                }}
                              />
                            </span>
                          </Whisper>
                        );
                      })()}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 10,
                      }}
                    >
                      {committedLabel ? <span>{committedLabel}</span> : null}
                      <span className={flashFillRun ? 'ppc-flash-fill-preview-hide' : undefined}>{cost}</span>
                      {flashFillRun ? (
                        <span className="ppc-flash-fill-preview-only">
                          {formatCost(getRateForMonth(rowData, ms), flashFillRun.value)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              }}
            </Cell>
          </Column>
        ))}

        <Column width={95} fixed="right" align="right">
          <HeaderCell>Total (h)</HeaderCell>
          <Cell>
            {(rowData) => {
              const getWindowServerTotal = (row) => monthStarts.reduce((sum, ms) => sum + Number(row?.hours?.[ms] ?? 0), 0);
              const getWindowEffectiveTotal = (row) => monthStarts.reduce((sum, ms) => sum + Number(getEffectiveHours(row, ms) ?? 0), 0);

              let total;
              if (!allTimeTotals) {
                total = isTotalRow(rowData)
                  ? monthStarts.reduce((sum, ms) => sum + Number(rowData?.hours?.[ms] ?? 0), 0)
                  : getWindowEffectiveTotal(rowData);
              } else if (isTotalRow(rowData)) {
                const baseOverall = Number(allTimeTotals?.totalHours ?? 0);
                const deltaOverall = rows
                  .filter((r) => !isTotalRow(r))
                  .reduce((sum, r) => sum + (getWindowEffectiveTotal(r) - getWindowServerTotal(r)), 0);
                total = baseOverall + deltaOverall;
              } else {
                const uid = Number(rowData?.userId);
                const baseUser = Number(allTimeTotalHoursByUserId.get(uid) ?? 0);
                const deltaUser = getWindowEffectiveTotal(rowData) - getWindowServerTotal(rowData);
                total = baseUser + deltaUser;
              }

              return (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: isTotalRow(rowData) ? 800 : 700 }}>
                    {Number(total ?? 0).toFixed(2)}h
                  </span>
                </div>
              );
            }}
          </Cell>
        </Column>
      </Table>
      </div>

    </Panel>
  );
}

const MemoViewAllActivityForecastSection = memo(ViewAllActivityForecastSection);

export default function ActivityHourForecastPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isViewTogglePending, startViewToggleTransition] = useTransition();

  const [printMode, setPrintMode] = useState(false);
  const [viewAllActivities, setViewAllActivities] = useState(false);
  // View All stacks many activity tables, so the page header and month controls are frozen while
  // scrolling; the controls band sticks just below the header's measured height.
  const [pageHeaderEl, setPageHeaderEl] = useState(null);
  const [pageHeaderH, setPageHeaderH] = useState(0);
  useLayoutEffect(() => {
    if (!pageHeaderEl) return undefined;
    const measure = () => setPageHeaderH(Math.round(pageHeaderEl.getBoundingClientRect().height));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(pageHeaderEl);
    return () => ro.disconnect();
  }, [pageHeaderEl]);

  const projectIdParam = Number(params?.projectId);
  const taskOrderIdParam = Number(params?.taskOrderId);

  const taskId = Number(params?.taskId);
  const activityId = Number(params?.activityId);

  const [start, setStart] = useState(() => readStartFromSearchParams(searchParams) ?? toMonthStartIso(new Date()));
  const [filterMonthsWithHours, setFilterMonthsWithHours] = useState(false);

  const { data: task } = useTask(Number.isInteger(taskId) ? taskId : null);

  const projectId = Number.isInteger(Number(task?.projectId)) ? Number(task.projectId) : null;
  const taskOrderId = Number.isInteger(Number(task?.taskOrderId))
    ? Number(task.taskOrderId)
    : (Number.isInteger(taskOrderIdParam) ? taskOrderIdParam : null);
  const projectLookupId = Number.isInteger(projectId)
    ? projectId
    : (Number.isInteger(projectIdParam) ? projectIdParam : null);

  const { data: projects } = useProjects({ enabled: Number.isInteger(projectLookupId) });

  // Forecast cells need Forecast hours on the project; supporters need Manage Disciplines.
  const permissions = usePermissions();
  const canForecast = permissions.canProject(projectLookupId, 'disciplines');
  const canManageDisciplines = permissions.canProject(projectLookupId, 'disciplines');

  const project = useMemo(() => {
    if (!Number.isInteger(projectLookupId)) return null;
    const list = Array.isArray(projects) ? projects : projects?.projects;
    const arr = Array.isArray(list) ? list : [];
    return arr.find((p) => Number(p?.id) === Number(projectLookupId)) || null;
  }, [projectLookupId, projects]);

  const projectDurationStartMonth = useMemo(() => {
    return normalizeMonthStart(project?.startDate ?? project?.start_date ?? project?.start ?? null);
  }, [project]);

  const projectDurationEndMonth = useMemo(() => {
    return normalizeMonthStart(project?.endDate ?? project?.end_date ?? project?.end ?? null);
  }, [project]);

  const projectRange = useMemo(() => {
    const startMonth = projectDurationStartMonth;
    const endMonth = projectDurationEndMonth;

    if (startMonth && endMonth && startMonth > endMonth) {
      return { startMonth: endMonth, endMonth: startMonth };
    }
    return { startMonth, endMonth };
  }, [projectDurationEndMonth, projectDurationStartMonth]);

  // Keeps the window start inside the project. Contiguous windows stop once the project's last month
  // reaches the last column, so stepping forward never leaves the table part-empty; the sparse view
  // pages through its own list and only needs the plain project bounds.
  const clampStartToProject = useCallback((monthStartIso) => {
    let maxStart = projectRange.endMonth;
    if (!filterMonthsWithHours && maxStart) {
      maxStart = addMonths(maxStart, -(WINDOW_SIZE - 1));
      if (projectRange.startMonth && maxStart < projectRange.startMonth) maxStart = projectRange.startMonth;
    }
    return clampMonthToRange(monthStartIso, projectRange.startMonth, maxStart);
  }, [filterMonthsWithHours, projectRange.endMonth, projectRange.startMonth]);

  const taskOrderPath = useMemo(() => {
    if (!Number.isInteger(projectLookupId)) return null;
    if (!Number.isInteger(taskOrderId)) return null;
    return `/projects/${projectLookupId}/task-orders/${taskOrderId}`;
  }, [projectLookupId, taskOrderId]);

  const disciplineDetailPath = useMemo(() => {
    if (!Number.isInteger(taskId)) return null;
    if (Number.isInteger(projectLookupId) && Number.isInteger(taskOrderId)) {
      return `/projects/${projectLookupId}/task-orders/${taskOrderId}/tasks/${taskId}`;
    }
    return `/tasks/${taskId}`;
  }, [projectLookupId, taskId, taskOrderId]);

  const { data: taskOrders = [] } = useTaskOrders(projectLookupId, {
    enabled: Number.isInteger(projectLookupId),
  });

  const selectedTaskOrder = useMemo(() => {
    const rows = Array.isArray(taskOrders) ? taskOrders : [];
    if (!Number.isInteger(taskOrderId)) return null;
    return rows.find((row) => Number(row?.id) === Number(taskOrderId)) || null;
  }, [taskOrderId, taskOrders]);

  // Follows the URL's start (links, back/forward, project bounds). It only acts when the URL-derived
  // start changes: month stepping sets start ahead of the URL (see commitStart), and must not be pulled
  // back while the URL catches up.
  const lastUrlStartRef = useRef(null);
  useEffect(() => {
    const desired = clampStartToProject(readStartFromSearchParams(searchParams));
    if (desired === lastUrlStartRef.current) return;
    lastUrlStartRef.current = desired;
    if (desired && desired !== start) setStart(desired);
  }, [clampStartToProject, searchParams, start]);

  const {
    data: activitySummary,
    isLoading: activitySummaryLoading,
    error: activitySummaryError,
  } = useProjectActivityForecastSummary(projectId, null, {
    enabled: Number.isInteger(projectId),
  });

  const baseEnabled = Number.isInteger(taskId) && Number.isInteger(activityId) && !!start;

  const {
    data: monthsWithHoursRes,
    isLoading: monthsWithHoursLoading,
    isFetching: monthsWithHoursFetching,
  } = useActivityHourForecastMonthsWithHours(
    Number.isInteger(taskId) ? taskId : null,
    Number.isInteger(activityId) ? activityId : null,
    { enabled: baseEnabled && filterMonthsWithHours }
  );

  const monthsWithHours = useMemo(() => {
    const arr = Array.isArray(monthsWithHoursRes?.months) ? monthsWithHoursRes.months : [];
    const inRange = filterMonthsToRange(arr, projectRange.startMonth, projectRange.endMonth);
    // API order can vary between fetches; keep this list canonical to avoid URL/start
    // sync effects bouncing between different "first" months.
    return Array.from(new Set(inRange)).sort((a, b) => String(a).localeCompare(String(b)));
  }, [monthsWithHoursRes?.months, projectRange.endMonth, projectRange.startMonth]);

  const sparseWindowActive = filterMonthsWithHours && monthsWithHours.length > 0;

  const sparseMonthStarts = useMemo(() => {
    if (!sparseWindowActive) return [];
    const startIdx = getSparseWindowStartIdx(monthsWithHours, start);
    return monthsWithHours.slice(startIdx, startIdx + WINDOW_SIZE);
  }, [monthsWithHours, sparseWindowActive, start]);

  // The window starting at `windowStart` plus `extra` months on each side (in the sparse view, `extra`
  // months with hours).
  const getMonthsAroundWindow = useCallback((windowStart, extra) => {
    if (sparseWindowActive) {
      const startIdx = getSparseWindowStartIdx(monthsWithHours, windowStart);
      return monthsWithHours.slice(Math.max(0, startIdx - extra), startIdx + WINDOW_SIZE + extra);
    }
    const out = [];
    for (let i = -extra; i < WINDOW_SIZE + extra; i += 1) {
      const ms = addMonths(windowStart, i);
      if (ms) out.push(ms);
    }
    return out;
  }, [monthsWithHours, sparseWindowActive]);

  // The displayed months plus one neighbour on each side, which the slide brings into view.
  const fetchedMonthStarts = useMemo(() => getMonthsAroundWindow(start, 1), [getMonthsAroundWindow, start]);

  const {
    data: forecast,
    isLoading: forecastBlocksLoading,
    isFetching: forecastBlocksFetching,
    error: forecastError,
  } = useActivityHourForecastBlocks(
    Number.isInteger(taskId) ? taskId : null,
    Number.isInteger(activityId) ? activityId : null,
    fetchedMonthStarts,
    { enabled: baseEnabled }
  );

  // While the months-with-hours list loads, the filtered view has nothing to show yet.
  const waitingForSparseMonths = filterMonthsWithHours && !sparseWindowActive;
  const forecastLoading = forecastBlocksLoading || (waitingForSparseMonths && monthsWithHoursLoading);
  const forecastFetching = forecastBlocksFetching || (waitingForSparseMonths && monthsWithHoursFetching);

  const forecastBusy = Boolean(forecastLoading || forecastFetching);

  const { data: allTimeTotals } = useActivityHourForecastTotals(
    Number.isInteger(taskId) ? taskId : null,
    Number.isInteger(activityId) ? activityId : null,
    { enabled: Number.isInteger(taskId) && Number.isInteger(activityId) }
  );

  const allTimeTotalHoursByUserId = useMemo(() => {
    const out = new Map();
    const list = Array.isArray(allTimeTotals?.rows) ? allTimeTotals.rows : [];
    for (const r of list) {
      const uid = Number(r?.userId);
      if (!Number.isInteger(uid)) continue;
      out.set(uid, Number(r?.totalHours ?? 0));
    }
    return out;
  }, [allTimeTotals?.rows]);

  const bulkUpsert = useBulkUpsertActivityHourForecast({
    // Keep UX quiet; the Save button spinner is enough.
  });

  const addSupporter = useAddActivitySupporter({
    // Toasts would be noisy here; the row list updating is enough.
  });

  const setSupporters = useSetActivitySupporters({
    // Toasts would be noisy here; the row list updating is enough.
  });

  const {
    data: usersRes,
    isLoading: usersLoading,
    error: usersError,
  } = useUsers();
  const userFteById = useMemo(() => {
    const out = new Map();
    const list = Array.isArray(usersRes) ? usersRes : [];
    for (const u of list) {
      const id = Number(u?.id);
      if (!Number.isInteger(id)) continue;
      const raw = Number(u?.ftePercent ?? u?.fte_percent ?? 100);
      const fte = Number.isFinite(raw) ? raw : 100;
      out.set(id, fte);
    }
    return out;
  }, [usersRes]);

  const contiguousMonthStarts = useMemo(() => {
    const out = [];
    for (let i = 0; i < WINDOW_SIZE; i += 1) {
      const ms = addMonths(start, i);
      if (ms) out.push(ms);
    }
    return out;
  }, [start]);

  const monthStartsForTable = useMemo(() => {
    const base = sparseWindowActive ? sparseMonthStarts : contiguousMonthStarts;
    return filterMonthsToRange(base, projectRange.startMonth, projectRange.endMonth);
  }, [contiguousMonthStarts, projectRange.endMonth, projectRange.startMonth, sparseMonthStarts, sparseWindowActive]);

  const viewAllMonthStarts = useMemo(() => {
    const out = [];
    for (let i = 0; i < WINDOW_SIZE; i += 1) {
      const ms = addMonths(start, i);
      if (ms) out.push(ms);
    }
    return filterMonthsToRange(out, projectRange.startMonth, projectRange.endMonth);
  }, [projectRange.endMonth, projectRange.startMonth, start]);

  const {
    data: viewAllBatchForecast,
    isLoading: viewAllBatchForecastLoading,
    error: viewAllBatchForecastError,
  } = useTaskActivitiesHourForecast(
    Number.isInteger(taskId) ? taskId : null,
    String(start || '').slice(0, 10),
    WINDOW_SIZE,
    {
      enabled: viewAllActivities && Number.isInteger(taskId) && !!start,
    }
  );

  const {
    data: viewAllBatchTotals,
    isLoading: viewAllBatchTotalsLoading,
    error: viewAllBatchTotalsError,
  } = useTaskActivitiesHourForecastTotals(
    Number.isInteger(taskId) ? taskId : null,
    {
      enabled: viewAllActivities && Number.isInteger(taskId),
    }
  );

  const viewAllBatchForecastByActivityId = useMemo(() => {
    const out = new Map();
    const list = Array.isArray(viewAllBatchForecast?.activities) ? viewAllBatchForecast.activities : [];
    for (const activity of list) {
      const aId = Number(activity?.activityId);
      if (!Number.isInteger(aId)) continue;
      out.set(aId, activity);
    }
    return out;
  }, [viewAllBatchForecast?.activities]);

  const viewAllBatchTotalsByActivityId = useMemo(() => {
    const out = new Map();
    const list = Array.isArray(viewAllBatchTotals?.activities) ? viewAllBatchTotals.activities : [];
    for (const activity of list) {
      const aId = Number(activity?.activityId);
      if (!Number.isInteger(aId)) continue;
      out.set(aId, activity);
    }
    return out;
  }, [viewAllBatchTotals?.activities]);

  const useBatchForViewAllSections = useMemo(() => {
    return !(viewAllBatchForecastError || viewAllBatchTotalsError);
  }, [viewAllBatchForecastError, viewAllBatchTotalsError]);

  const currentMonthStartIso = useMemo(() => normalizeMonthStart(toMonthStartIso(new Date())), []);
  const [pastForecastOverrideEnabled, setPastForecastOverrideEnabled] = useState(false);
  const pastForecastUnlockAttemptCountRef = useRef(0);
  const [pastForecastOverrideModalOpen, setPastForecastOverrideModalOpen] = useState(false);

  const isMonthFullyInPast = useCallback((monthStartIso) => {
    const normalized = normalizeMonthStart(monthStartIso);
    if (!normalized || !currentMonthStartIso) return false;
    return normalized < currentMonthStartIso;
  }, [currentMonthStartIso]);

  const isPastMonthLocked = useCallback(
    (monthStartIso) => isMonthFullyInPast(monthStartIso) && !pastForecastOverrideEnabled,
    [isMonthFullyInPast, pastForecastOverrideEnabled]
  );

  const registerPastForecastUnlockAttempt = useCallback(() => {
    if (pastForecastOverrideEnabled) return;
    const next = Number(pastForecastUnlockAttemptCountRef.current || 0) + 1;
    if (next >= PAST_FORECAST_OVERRIDE_CLICK_THRESHOLD) {
      pastForecastUnlockAttemptCountRef.current = 0;
      setPastForecastOverrideModalOpen(true);
      return;
    }
    pastForecastUnlockAttemptCountRef.current = next;
  }, [pastForecastOverrideEnabled]);

  const warningsReady = useMemo(() => {
    if (forecastBusy) return false;
    if (!forecast || !Array.isArray(forecast?.months)) return false;

    // The fetch also carries the neighbouring months, so only require every displayed month to be in it.
    const forecastMonths = new Set(forecast.months.map((m) => normalizeMonthStart(m?.monthStart ?? m)).filter(Boolean));
    return monthStartsForTable.every((ms) => forecastMonths.has(ms));
  }, [forecast, forecastBusy, monthStartsForTable]);

  const [warningsVisible, setWarningsVisible] = useState(false);
  const warningsTimerRef = useRef(null);

  const warningsKey = useMemo(() => {
    const baseStart = String(start || '').slice(0, 10);
    return `${taskId}|${activityId}|${baseStart}|${filterMonthsWithHours ? 1 : 0}|${monthStartsForTable.join(',')}`;
  }, [activityId, filterMonthsWithHours, monthStartsForTable, start, taskId]);

  // Never show warning icons during a range transition. Once the forecast is stable for a moment,
  // allow warnings to render. This avoids a brief flash when data is still settling.
  useEffect(() => {
    setWarningsVisible(false);
  }, [warningsKey]);

  useEffect(() => {
    if (warningsTimerRef.current) {
      clearTimeout(warningsTimerRef.current);
      warningsTimerRef.current = null;
    }

    if (!warningsReady) return;

    warningsTimerRef.current = setTimeout(() => {
      setWarningsVisible(true);
      warningsTimerRef.current = null;
    }, 300);

    return () => {
      if (warningsTimerRef.current) {
        clearTimeout(warningsTimerRef.current);
        warningsTimerRef.current = null;
      }
    };
  }, [warningsKey, warningsReady]);

  const rangeLabel = useMemo(() => {
    if (monthStartsForTable.length === 0) return '';
    const first = formatMonthLabel(monthStartsForTable[0]);
    const last = formatMonthLabel(monthStartsForTable[monthStartsForTable.length - 1]);
    return first && last ? `${first} – ${last}` : '';
  }, [monthStartsForTable]);

  const draftByKeyRef = useRef({});
  const draftInputByKeyRef = useRef({});
  const [draftVersion, setDraftVersion] = useState(0);
  const [savingAll, setSavingAll] = useState(false);

  const [editingSupports, setEditingSupports] = useState(false);
  const [addSupportUserId, setAddSupportUserId] = useState(null);
  const [removeSupportOpen, setRemoveSupportOpen] = useState(false);
  const [removeSupportUser, setRemoveSupportUser] = useState(null);

  const discardAllDrafts = useCallback(() => {
    draftByKeyRef.current = {};
    draftInputByKeyRef.current = {};
    setDraftVersion((x) => x + 1);
  }, []);

  const getDraftKey = (userId, monthStart) => `${userId}|${monthStart}`;

  const getRateForMonth = (row, monthStart) => {
    if (!row || !monthStart) return null;

    const ratesMap = row?.rates;
    const hasMonthRatesMap = Boolean(
      ratesMap && typeof ratesMap === 'object' && Object.keys(ratesMap).length > 0
    );

    if (hasMonthRatesMap) {
      const hasKey = Object.prototype.hasOwnProperty.call(ratesMap, monthStart);
      if (!hasKey) return null;
      const raw = ratesMap[monthStart];
      if (raw === null || raw === undefined || raw === '') return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }

    const fallback = Number(row?.rate);
    return Number.isFinite(fallback) ? fallback : null;
  };

  const getRateEffectiveDateForMonth = (row, monthStart) => {
    const s = String(row?.rateEffectiveDates?.[monthStart] ?? '').slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
  };

  const getEffectiveHours = (row, monthStart) => {
    const key = getDraftKey(row.userId, monthStart);
    const draft = draftByKeyRef.current[key];
    if (draft !== undefined) return draft;
    return Number(row?.hours?.[monthStart] ?? 0);
  };

  const getDisplayHours = (row, monthStart) => {
    const key = getDraftKey(row.userId, monthStart);
    if (key in draftInputByKeyRef.current) return draftInputByKeyRef.current[key];
    const draft = draftByKeyRef.current[key];
    if (draft !== undefined) return draft;
    return Number(row?.hours?.[monthStart] ?? 0);
  };

  const rows = useMemo(() => {
    const base = Array.isArray(forecast?.rows) ? forecast.rows : [];
    const normalized = base
      .filter((r) => Number.isInteger(Number(r?.userId)))
      .map((r) => ({
        userId: Number(r.userId),
        userName: r.userName || '—',
        role: r.role || 'support',
        rate: r.rate,
        rates: r.rates || {},
        rateEffectiveDates: r.rateEffectiveDates || r.rate_effective_dates || {},
        hours: r.hours || {},
        committedHours: r.committedHours || r.committed_hours || {},
      }));

    // Ensure lead row first.
    normalized.sort((a, b) => {
      const aLead = a.role === 'lead' ? 0 : 1;
      const bLead = b.role === 'lead' ? 0 : 1;
      if (aLead !== bLead) return aLead - bLead;
      return String(a.userName).localeCompare(String(b.userName));
    });

    // Totals row (computed using drafts too): both hours + cost. Covers the neighbouring months too,
    // which the slide shows on its way in.
    const totals = {};
    const totalCosts = {};
    for (const ms of fetchedMonthStarts) {
      const totalHours = normalized.reduce((sum, row) => sum + Number(getEffectiveHours(row, ms) ?? 0), 0);
      totals[ms] = totalHours;

      totalCosts[ms] = normalized.reduce((sum, row) => {
        const h = Number(getEffectiveHours(row, ms) ?? 0);
        const r = getRateForMonth(row, ms);
        if (!Number.isFinite(h) || !Number.isFinite(Number(r))) return sum;
        return sum + Number(r) * h;
      }, 0);
    }

    return [
      ...normalized,
      {
        userId: -1,
        userName: 'Total',
        role: 'total',
        rate: null,
        rates: {},
          rateEffectiveDates: {},
        hours: totals,
        costs: totalCosts,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecast?.rows, fetchedMonthStarts, draftVersion]);

  const rowUserIds = useMemo(() => collectRowUserIds(rows), [rows]);
  const { timeOffHoursByUserMonth, timeOffError } = useTimeOffHoursByUserMonth(rowUserIds, fetchedMonthStarts);

  const isTotalRow = (rowData) => Number(rowData?.userId) === -1 || rowData?.role === 'total';

  const commitCell = (row, monthStart) => {
    if (isTotalRow(row)) return;
    if (isPastMonthLocked(monthStart)) return;

    const key = getDraftKey(row.userId, monthStart);
    const raw = key in draftInputByKeyRef.current ? draftInputByKeyRef.current[key] : undefined;
    const parsed = raw === '' || raw === null || raw === undefined ? 0 : Number(raw);
    const normalized = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    const snapped = snapToQuarter(normalized);

    const current = Number(row?.hours?.[monthStart] ?? 0);
    if (snapped === current) {
      delete draftByKeyRef.current[key];
      delete draftInputByKeyRef.current[key];
    } else {
      draftByKeyRef.current[key] = snapped;
      draftInputByKeyRef.current[key] = snapped;
    }
    setDraftVersion((x) => x + 1);
  };

  const onChangeCell = (row, monthStart, value) => {
    if (isTotalRow(row)) return;
    if (isPastMonthLocked(monthStart)) return;

    const raw = value;
    const key = getDraftKey(row.userId, monthStart);
    draftInputByKeyRef.current[key] = raw;

    const v = raw === '' || raw === null || raw === undefined ? 0 : Number(raw);
    const normalized = Number.isFinite(v) ? Math.max(0, v) : 0;
    const snapped = snapToQuarter(normalized);

    const current = Number(row?.hours?.[monthStart] ?? 0);

    if (snapped === current) delete draftByKeyRef.current[key];
    else draftByKeyRef.current[key] = snapped;

    setDraftVersion((x) => x + 1);
  };

  const flashFillRunsByUserId = useMemo(
    () => buildFlashFillCellRuns(rows.filter((r) => !isTotalRow(r)), monthStartsForTable, getEffectiveHours, isPastMonthLocked),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, monthStartsForTable, isPastMonthLocked]
  );

  const { applyFlashFill, clearFlashFillUndo } = useFlashFillDrafts({
    draftByKeyRef,
    draftInputByKeyRef,
    setDraftVersion,
    disabled: savingAll,
  });

  const dirtyEdits = useMemo(() => {
    const base = Array.isArray(forecast?.rows) ? forecast.rows : [];
    const dirty = [];
    const drafts = draftByKeyRef.current;

    for (const row of base) {
      const userId = Number(row?.userId);
      if (!Number.isInteger(userId)) continue;

      for (const ms of monthStartsForTable) {
        if (isPastMonthLocked(ms)) continue;

        const key = getDraftKey(userId, ms);
        if (!(key in drafts)) continue;

        const next = Number(drafts[key] ?? 0);
        const current = Number(row?.hours?.[ms] ?? 0);
        if (next === current) continue;

        dirty.push({ userId, monthStart: ms, hours: Number.isFinite(next) ? next : 0 });
      }
    }

    return dirty;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecast?.rows, monthStartsForTable, draftVersion, currentMonthStartIso, pastForecastOverrideEnabled]);

  const hasUnsavedChanges = dirtyEdits.length > 0;

  // Keep the current month window within project duration bounds.
  useEffect(() => {
    if (hasUnsavedChanges) return;

    const currentStart = normalizeMonthStart(start);
    const boundedStart = clampStartToProject(currentStart);
    if (!boundedStart || boundedStart === currentStart) return;

    const currentUrlStart = readStartFromSearchParams(searchParams);
    if (currentUrlStart === boundedStart) return;

    const next = new URLSearchParams(searchParams);
    next.set('start', boundedStart);
    setSearchParams(next, { replace: true });
  }, [clampStartToProject, hasUnsavedChanges, searchParams, setSearchParams, start]);

  // In sparse mode, keep the URL/start aligned to the current page start so Prev/Next paging works.
  useEffect(() => {
    if (!filterMonthsWithHours) return;
    if (hasUnsavedChanges) return;
    const desiredRaw = sparseMonthStarts?.[0];
    const desired = normalizeMonthStart(desiredRaw);
    if (!desired) return;

    const currentUrlStart = normalizeMonthStart(readStartFromSearchParams(searchParams));
    const currentStart = normalizeMonthStart(start);
    if (currentUrlStart === desired && currentStart === desired) return;

    const next = new URLSearchParams(searchParams);
    next.set('start', desired);
    setSearchParams(next, { replace: true });
  }, [filterMonthsWithHours, hasUnsavedChanges, searchParams, setSearchParams, sparseMonthStarts, start]);

  const leadUserId = useMemo(() => {
    const lead = rows.find((r) => !isTotalRow(r) && r?.role === 'lead');
    const id = Number(lead?.userId);
    return Number.isInteger(id) ? id : null;
  }, [rows]);

  const supportUserIds = useMemo(() => {
    return rows
      .filter((r) => !isTotalRow(r) && r?.role !== 'lead')
      .map((r) => Number(r?.userId))
      .filter((id) => Number.isInteger(id));
  }, [rows]);

  const addSupportOptions = useMemo(() => {
    const existing = new Set(supportUserIds);
    const lead = Number.isInteger(leadUserId) ? Number(leadUserId) : null;

    const list = Array.isArray(usersRes) ? usersRes : [];

    return list
      .filter((u) => {
        const id = Number(u?.id);
        if (!Number.isInteger(id)) return false;
        if (Number.isInteger(lead) && id === lead) return false;
        if (existing.has(id)) return false;
        return true;
      })
      .map((u) => ({ label: u.name, value: Number(u.id) }));
  }, [leadUserId, supportUserIds, usersRes]);

  const enterSupportEditMode = () => {
    if (editingSupports) return;
    requestDiscardConfirmation(() => {
      setEditingSupports(true);
    }, 'single');
  };

  const exitSupportEditMode = () => {
    setEditingSupports(false);
    setAddSupportUserId(null);
    setRemoveSupportOpen(false);
    setRemoveSupportUser(null);
  };

  const requestRemoveSupport = (rowData) => {
    requestDiscardConfirmation(() => {
      const userId = Number(rowData?.userId);
      if (!Number.isInteger(userId)) return;
      if (rowData?.role === 'lead') return;
      if (isTotalRow(rowData)) return;

      setRemoveSupportUser({ userId, userName: rowData?.userName || '—' });
      setRemoveSupportOpen(true);
    }, 'single');
  };

  const confirmRemoveSupport = async () => {
    requestDiscardConfirmation(async () => {
      const uid = Number(removeSupportUser?.userId);
      if (!Number.isInteger(taskId) || !Number.isInteger(activityId) || !Number.isInteger(uid)) return;

      const nextUserIds = supportUserIds.filter((id) => id !== uid);
      await setSupporters.mutateAsync({ taskId, activityId, userIds: nextUserIds });

      setRemoveSupportOpen(false);
      setRemoveSupportUser(null);
    }, 'single');
  };

  const submitAddSupport = async () => {
    requestDiscardConfirmation(async () => {
      const uid = Number(addSupportUserId);
      if (!Number.isInteger(taskId) || !Number.isInteger(activityId) || !Number.isInteger(uid)) return;

      await addSupporter.mutateAsync({ taskId, activityId, userId: uid });
      setAddSupportUserId(null);
    }, 'single');
  };

  // The window start one step back (-1) or forward (+1): a calendar month, or in the sparse view the
  // neighbouring month with hours.
  const getStepStartCandidate = useCallback((step) => {
    if (sparseWindowActive) {
      const startIdx = getSparseWindowStartIdx(monthsWithHours, start);
      const maxStartIdx = Math.max(0, monthsWithHours.length - WINDOW_SIZE);
      return monthsWithHours[Math.min(maxStartIdx, Math.max(0, startIdx + step))] || null;
    }
    return clampStartToProject(addMonths(start, step));
  }, [clampStartToProject, monthsWithHours, sparseWindowActive, start]);

  const normalizeNavigatedStart = useCallback((inputStart) => {
    const ns = normalizeMonthStart(inputStart);
    return ns ? clampStartToProject(ns) : null;
  }, [clampStartToProject]);

  const normalizedStart = useMemo(() => normalizeNavigatedStart(start), [normalizeNavigatedStart, start]);

  const prevStartCandidate = useMemo(
    () => normalizeNavigatedStart(getStepStartCandidate(-1)),
    [getStepStartCandidate, normalizeNavigatedStart]
  );

  const nextStartCandidate = useMemo(
    () => normalizeNavigatedStart(getStepStartCandidate(1)),
    [getStepStartCandidate, normalizeNavigatedStart]
  );

  const prevRangeBlocked = Boolean(prevStartCandidate && normalizedStart && prevStartCandidate === normalizedStart);
  const nextRangeBlocked = Boolean(nextStartCandidate && normalizedStart && nextStartCandidate === normalizedStart);

  // The month a one-step slide brings into view on each side. Both are part of the current fetch and
  // render in off-screen columns, so the slide shows real data.
  const slideInMonths = useMemo(() => {
    const prev = prevRangeBlocked ? null : prevStartCandidate;
    let next = null;
    if (!nextRangeBlocked && nextStartCandidate) {
      next = sparseWindowActive
        ? monthsWithHours[getSparseWindowStartIdx(monthsWithHours, nextStartCandidate) + WINDOW_SIZE - 1] ?? null
        : addMonths(nextStartCandidate, WINDOW_SIZE - 1);
    }
    return { prev, next };
  }, [monthsWithHours, nextRangeBlocked, nextStartCandidate, prevRangeBlocked, prevStartCandidate, sparseWindowActive]);

  const prefetchActivityHourForecastBlocks = usePrefetchActivityHourForecastBlocks();

  // Keep the months within MONTH_PREFETCH_RANGE of the window loaded (plus the months the slide shows
  // beyond them), so stepping through them never waits on the network.
  useEffect(() => {
    if (!baseEnabled || viewAllActivities) return;
    prefetchActivityHourForecastBlocks({
      taskId,
      activityId,
      months: getMonthsAroundWindow(start, MONTH_PREFETCH_RANGE + 1),
    });
  }, [activityId, baseEnabled, getMonthsAroundWindow, prefetchActivityHourForecastBlocks, start, taskId, viewAllActivities]);

  const tableWrapRef = useRef(null);
  // { from, className } while the month columns slide away from the window at `from`. The slide is
  // driven straight on the DOM rather than through state, so a click starts moving immediately instead
  // of first waiting for this (large) page to re-render.
  const monthSlideRef = useRef(null);
  // Arrow clicks made mid-slide (net months, signed); they play out once the current slide lands.
  const queuedMonthStepsRef = useRef(0);
  const urlStartTimerRef = useRef(null);

  const endMonthSlide = useCallback(() => {
    const slide = monthSlideRef.current;
    if (!slide) return;
    monthSlideRef.current = null;
    tableWrapRef.current?.classList.remove(slide.className);
  }, []);

  // Drop the slide offset in the same commit that renders the new window (before paint), so the old
  // months never flash back into place. Any change of start ends a slide, including ones from elsewhere.
  useLayoutEffect(() => {
    if (monthSlideRef.current && normalizeMonthStart(start) !== monthSlideRef.current.from) endMonthSlide();
  }, [endMonthSlide, start]);

  // A slide or URL write in flight belongs to this activity; abandon it when switching activity or
  // leaving the page.
  useEffect(() => () => {
    endMonthSlide();
    queuedMonthStepsRef.current = 0;
    clearTimeout(urlStartTimerRef.current);
  }, [activityId, endMonthSlide, taskId]);

  // Shows the window at `ns` right away by setting start directly; the URL follows once clicking
  // settles, since every URL change re-renders the whole routed page. The URL sync effect tolerates
  // start running ahead of the URL in the meantime.
  const commitStart = (ns) => {
    setStart(ns);
    clearTimeout(urlStartTimerRef.current);
    urlStartTimerRef.current = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('start', ns);
        return next;
      }, { replace: true });
    }, URL_START_DEBOUNCE_MS);
  };

  // Slides every month column one column over (the incoming month is already rendered just out of
  // view), then switches the window once the slide has run and the new window's data is cached.
  const slideToStart = (ns, direction) => {
    const wrap = tableWrapRef.current;
    const monthCell = wrap?.querySelector('.rs-table-cell.ppc-forecast-month-col:not(.ppc-forecast-month-ghost)');
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (!monthCell || reduceMotion) {
      commitStart(ns);
      return;
    }

    // Measuring also flushes styles, so a slide straight after the previous one starts from rest.
    wrap.style.setProperty('--ppc-forecast-month-w', `${monthCell.getBoundingClientRect().width}px`);
    wrap.style.setProperty('--ppc-forecast-slide-ms', `${MONTH_SLIDE_MS}ms`);

    const slide = { from: normalizeMonthStart(start), className: `ppc-forecast-table-wrap--slide-${direction}` };
    monthSlideRef.current = slide;
    wrap.classList.add(slide.className);

    const slideFinished = new Promise((resolve) => {
      setTimeout(resolve, MONTH_SLIDE_MS);
    });
    const dataReady = prefetchActivityHourForecastBlocks({ taskId, activityId, months: getMonthsAroundWindow(ns, 1) });

    Promise.all([slideFinished, dataReady]).then(() => {
      if (monthSlideRef.current !== slide) return;
      commitStart(ns);
      // Safety net: never leave the columns shifted if start somehow doesn't change.
      setTimeout(() => {
        if (monthSlideRef.current === slide) endMonthSlide();
      }, 1000);
    });
  };

  const setStartWithConfirm = (nextStart) => {
    const ns = normalizeNavigatedStart(nextStart);
    if (!ns) return;
    if (monthSlideRef.current) return;
    if (ns === normalizeNavigatedStart(start)) return;

    // A single step either way slides; longer jumps (e.g. "Current") just switch.
    let direction = null;
    if (ns === prevStartCandidate && slideInMonths.prev) direction = 'prev';
    else if (ns === nextStartCandidate && slideInMonths.next) direction = 'next';

    requestDiscardConfirmation(() => {
      if (direction && !viewAllActivities) slideToStart(ns, direction);
      else commitStart(ns);
    }, viewAllActivities ? 'view-all' : 'single');
  };

  const stepMonths = (step) => {
    if (monthSlideRef.current) {
      queuedMonthStepsRef.current += step;
      return;
    }
    const candidate = step < 0 ? prevStartCandidate : nextStartCandidate;
    if (candidate) setStartWithConfirm(candidate);
  };

  const goPrevMonths = () => stepMonths(-1);
  const goNextMonths = () => stepMonths(1);

  // Play queued clicks one month at a time, each from the window the previous one landed on.
  useEffect(() => {
    const queued = queuedMonthStepsRef.current;
    if (monthSlideRef.current || queued === 0) return;

    const step = Math.sign(queued);
    const blocked = step < 0 ? prevRangeBlocked || !prevStartCandidate : nextRangeBlocked || !nextStartCandidate;
    if (blocked) {
      queuedMonthStepsRef.current = 0;
      return;
    }
    queuedMonthStepsRef.current -= step;
    stepMonths(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start]);

  const goCurrentMonths = () => {
    const current = toMonthStartIso(new Date());
    if (sparseWindowActive) {
      setStartWithConfirm(monthsWithHours[getSparseWindowStartIdx(monthsWithHours, current)]);
      return;
    }
    setStartWithConfirm(current);
  };

  const isOnCurrentWindow = useMemo(() => {
    const current = toMonthStartIso(new Date());
    if (filterMonthsWithHours && !sparseWindowActive) return false;

    const expectedStart = sparseWindowActive
      ? monthsWithHours[getSparseWindowStartIdx(monthsWithHours, current)]
      : clampStartToProject(current);
    return Boolean(expectedStart) && normalizeMonthStart(start) === expectedStart;
  }, [clampStartToProject, filterMonthsWithHours, monthsWithHours, sparseWindowActive, start]);

  const saveAll = async () => {
    if (!hasUnsavedChanges) return;
    if (!Number.isInteger(taskId) || !Number.isInteger(activityId)) return;
    if (savingAll) return;

    // Temporary development override should only last until the next save action.
    setPastForecastOverrideEnabled(false);
    pastForecastUnlockAttemptCountRef.current = 0;
    setPastForecastOverrideModalOpen(false);

    setSavingAll(true);
    try {
      await bulkUpsert.mutateAsync({ taskId, activityId, edits: dirtyEdits });

      for (const edit of dirtyEdits) {
        delete draftByKeyRef.current[getDraftKey(edit.userId, edit.monthStart)];
        delete draftInputByKeyRef.current[getDraftKey(edit.userId, edit.monthStart)];
      }
      clearFlashFillUndo();
      setDraftVersion((x) => x + 1);
    } finally {
      setSavingAll(false);
    }
  };

  // Warn on browser refresh/close.
  useEffect(() => {
    const handler = (e) => {
      if (!hasUnsavedChanges) return;
      e.preventDefault();
      // Chrome requires returnValue to be set.
      // eslint-disable-next-line no-param-reassign
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const activityOptions = useMemo(() => {
    const arr = Array.isArray(activitySummary?.activities) ? activitySummary.activities : [];

    if (!Number.isInteger(taskId)) return [];

    // Match Task Detail "#" ordering for activities on the current task.
    const orderByActivityId = new Map();
    const taskActivities = Array.isArray(task?.activities) ? task.activities : [];
    for (let i = 0; i < taskActivities.length; i += 1) {
      const id = Number(taskActivities[i]?.id);
      if (Number.isInteger(id)) orderByActivityId.set(id, i);
    }

    return arr
      .filter(
        (a) =>
          Number.isInteger(Number(a?.taskId)) &&
          Number.isInteger(Number(a?.activityId)) &&
          Number(a?.taskId) === Number(taskId)
      )
      .map((a) => {
        const totalHours = Number(a?.totalHours ?? 0);
        const lead = a?.leadName ? String(a.leadName) : '—';
        const desc = `Lead: ${lead} • ${Number.isFinite(totalHours) ? totalHours.toFixed(2) : '0.00'}h forecasted`;
        return {
          label: a?.activityName ? String(a.activityName) : `Activity #${Number(a.activityId)}`,
          value: `${Number(a.taskId)}|${Number(a.activityId)}`,
          description: desc,
          sortActivityId: Number(a.activityId),
          activityNumber: orderByActivityId.has(Number(a.activityId)) ? orderByActivityId.get(Number(a.activityId)) + 1 : null,
        };
      })
      .sort((a, b) => {
        const aIdx = orderByActivityId.get(Number(a.sortActivityId)) ?? Number.MAX_SAFE_INTEGER;
        const bIdx = orderByActivityId.get(Number(b.sortActivityId)) ?? Number.MAX_SAFE_INTEGER;
        if (aIdx !== bIdx) return aIdx - bIdx;
        return String(a.label).localeCompare(String(b.label));
      });
  }, [activitySummary?.activities, task?.activities, taskId]);

  const ACTIVITY_TOGGLE_WIDTH = 460;


  const selectedValue = useMemo(() => {
    if (!Number.isInteger(taskId) || !Number.isInteger(activityId)) return null;
    return `${taskId}|${activityId}`;
  }, [activityId, taskId]);

  const { data: closeDatesRes } = useCloseDates();
  const closeDates = useMemo(() => (Array.isArray(closeDatesRes) ? closeDatesRes : []), [closeDatesRes]);

  // The one source of truth for close dates: the month header, its tooltip and the working-hours
  // baseline all read this, so the dates listed can never disagree with the hours subtracted.
  const closeDatesByMonthStart = useMemo(() => {
    const out = new Map();
    for (const ms of fetchedMonthStarts) {
      out.set(String(ms).slice(0, 10), resolveCloseDatesInMonth(ms, closeDates));
    }
    return out;
  }, [closeDates, fetchedMonthStarts]);

  const workableHoursByMonthStart = useMemo(() => {
    const out = new Map();
    for (const ms of fetchedMonthStarts) {
      out.set(ms, getWorkingHoursInMonth(ms, closeDates));
    }
    return out;
  }, [closeDates, fetchedMonthStarts]);

  const printDisplayedTotals = useMemo(() => {
    const totalRow = rows.find((r) => isTotalRow(r));
    if (!totalRow) return { displayedHours: 0, displayedCost: 0 };

    let displayedHours = 0;
    let displayedCost = 0;
    for (const ms of monthStartsForTable) {
      const h = Number(totalRow?.hours?.[ms] ?? 0);
      const c = Number(totalRow?.costs?.[ms] ?? 0);
      displayedHours += Number.isFinite(h) ? h : 0;
      displayedCost += Number.isFinite(c) ? c : 0;
    }

    return { displayedHours, displayedCost };
  }, [monthStartsForTable, rows]);

  const activitySummaryByActivityId = useMemo(() => {
    const out = new Map();
    const list = Array.isArray(activitySummary?.activities) ? activitySummary.activities : [];
    for (const item of list) {
      const aTaskId = Number(item?.taskId);
      const aActivityId = Number(item?.activityId);
      if (!Number.isInteger(aTaskId) || !Number.isInteger(aActivityId)) continue;
      if (Number.isInteger(taskId) && aTaskId !== Number(taskId)) continue;
      out.set(aActivityId, item);
    }
    return out;
  }, [activitySummary?.activities, taskId]);

  const [viewAllSectionTotalsByActivityId, setViewAllSectionTotalsByActivityId] = useState({});
  const [viewAllSectionUnsavedByActivityId, setViewAllSectionUnsavedByActivityId] = useState({});
  const pendingDiscardActionRef = useRef(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [discardConfirmMode, setDiscardConfirmMode] = useState('single');

  const handleViewAllSectionTotalsChange = useCallback((sectionActivityId, totals) => {
    const aId = Number(sectionActivityId);
    if (!Number.isInteger(aId)) return;

    setViewAllSectionTotalsByActivityId((prev) => {
      if (!totals || (!Number.isFinite(Number(totals?.totalHours)) && !Number.isFinite(Number(totals?.totalCost)))) {
        if (!Object.prototype.hasOwnProperty.call(prev, aId)) return prev;
        const next = { ...prev };
        delete next[aId];
        return next;
      }

      const nextHours = Number.isFinite(Number(totals?.totalHours)) ? Number(totals.totalHours) : 0;
      const nextCost = Number.isFinite(Number(totals?.totalCost)) ? Number(totals.totalCost) : 0;
      const prevEntry = prev[aId];
      if (
        prevEntry &&
        Math.abs(Number(prevEntry.totalHours ?? 0) - nextHours) <= 0.0001 &&
        Math.abs(Number(prevEntry.totalCost ?? 0) - nextCost) <= 0.0001
      ) {
        return prev;
      }

      const next = { ...prev };
      next[aId] = {
        totalHours: nextHours,
        totalCost: nextCost,
      };
      return next;
    });
  }, []);

  const handleViewAllSectionUnsavedChange = useCallback((sectionActivityId, hasUnsaved) => {
    const aId = Number(sectionActivityId);
    if (!Number.isInteger(aId)) return;

    setViewAllSectionUnsavedByActivityId((prev) => {
      const alreadyUnsaved = prev[aId] === true;
      if (hasUnsaved && alreadyUnsaved) return prev;
      if (!hasUnsaved && !alreadyUnsaved) return prev;

      const next = { ...prev };
      if (hasUnsaved) {
        next[aId] = true;
      } else {
        delete next[aId];
      }
      return next;
    });
  }, []);

  const viewAllActivityRows = useMemo(() => {
    return activityOptions.map((opt) => {
      const aId = Number(opt?.sortActivityId);
      const summary = Number.isInteger(aId) ? activitySummaryByActivityId.get(aId) : null;
      return {
        activityId: Number.isInteger(aId) ? aId : null,
        activityName: String(opt?.label ?? ''),
        activityNumber: Number.isInteger(Number(opt?.activityNumber)) ? Number(opt.activityNumber) : null,
        totalHours: Number(summary?.totalHours ?? 0),
        totalCost: Number(summary?.totalCost ?? 0),
      };
    });
  }, [activityOptions, activitySummaryByActivityId]);

  const viewAllSectionPrefetchedLoading = viewAllBatchForecastLoading || viewAllBatchTotalsLoading;

  const viewAllSectionCards = useMemo(() => {
    return viewAllActivityRows.map((row) => {
      const sectionForecast = Number.isInteger(row.activityId)
        ? viewAllBatchForecastByActivityId.get(row.activityId)
        : null;
      const sectionTotals = Number.isInteger(row.activityId)
        ? viewAllBatchTotalsByActivityId.get(row.activityId)
        : null;

      return (
        <MemoViewAllActivityForecastSection
          key={`view-all-${row.activityId}`}
          taskId={taskId}
          activityId={row.activityId}
          activityName={row.activityName}
          activityNumber={row.activityNumber}
          start={start}
          monthStarts={viewAllMonthStarts}
          userFteById={userFteById}
          isPastMonthLocked={isPastMonthLocked}
          onLockedPastMonthAttempt={registerPastForecastUnlockAttempt}
          onTotalsChange={handleViewAllSectionTotalsChange}
          onUnsavedChange={handleViewAllSectionUnsavedChange}
          prefetchedForecast={sectionForecast || { rows: [], months: [] }}
          prefetchedAllTimeTotals={sectionTotals || null}
          prefetchedLoading={viewAllSectionPrefetchedLoading}
          usePrefetchedData={useBatchForViewAllSections}
          canForecast={canForecast}
        />
      );
    });
  }, [
    canForecast,
    handleViewAllSectionTotalsChange,
    handleViewAllSectionUnsavedChange,
    isPastMonthLocked,
    registerPastForecastUnlockAttempt,
    start,
    taskId,
    useBatchForViewAllSections,
    userFteById,
    viewAllActivityRows,
    viewAllBatchForecastByActivityId,
    viewAllBatchTotalsByActivityId,
    viewAllMonthStarts,
    viewAllSectionPrefetchedLoading,
  ]);

  const viewAllTotals = useMemo(() => {
    return viewAllActivityRows.reduce(
      (acc, row) => {
        const aId = Number(row?.activityId);
        const live = Number.isInteger(aId) ? viewAllSectionTotalsByActivityId[aId] : null;

        if (live && (Number.isFinite(Number(live?.totalHours)) || Number.isFinite(Number(live?.totalCost)))) {
          acc.totalHours += Number.isFinite(Number(live?.totalHours)) ? Number(live.totalHours) : 0;
          acc.totalCost += Number.isFinite(Number(live?.totalCost)) ? Number(live.totalCost) : 0;
        } else {
          acc.totalHours += Number.isFinite(Number(row?.totalHours)) ? Number(row.totalHours) : 0;
          acc.totalCost += Number.isFinite(Number(row?.totalCost)) ? Number(row.totalCost) : 0;
        }

        return acc;
      },
      { totalHours: 0, totalCost: 0 }
    );
  }, [viewAllActivityRows, viewAllSectionTotalsByActivityId]);

  const hasViewAllUnsavedChanges = useMemo(() => {
    return Object.values(viewAllSectionUnsavedByActivityId).some(Boolean);
  }, [viewAllSectionUnsavedByActivityId]);

  const authenticatedFetch = useAuthenticatedFetch();
  const [viewAllExportModalOpen, setViewAllExportModalOpen] = useState(false);
  const [viewAllExporting, setViewAllExporting] = useState(false);
  const [viewAllExportError, setViewAllExportError] = useState('');
  const [viewAllExportStartMonth, setViewAllExportStartMonth] = useState(() => getCurrentYearStartMonthIso());
  const [viewAllExportEndMonth, setViewAllExportEndMonth] = useState(() => addMonths(getCurrentYearStartMonthIso(), WINDOW_SIZE - 1) || getCurrentYearStartMonthIso());
  const [singleExportModalOpen, setSingleExportModalOpen] = useState(false);
  const [singleExporting, setSingleExporting] = useState(false);
  const [singleExportError, setSingleExportError] = useState('');
  const [singleExportStartMonth, setSingleExportStartMonth] = useState(() => getCurrentYearStartMonthIso());
  const [singleExportEndMonth, setSingleExportEndMonth] = useState(() => addMonths(getCurrentYearStartMonthIso(), WINDOW_SIZE - 1) || getCurrentYearStartMonthIso());

  const canExportViewAll = useMemo(() => {
    return (
      viewAllActivities &&
      !viewAllBatchForecastLoading &&
      !viewAllBatchTotalsLoading &&
      !viewAllBatchForecastError &&
      !viewAllBatchTotalsError &&
      !hasViewAllUnsavedChanges &&
      viewAllActivityRows.length > 0 &&
      viewAllMonthStarts.length > 0
    );
  }, [
    hasViewAllUnsavedChanges,
    viewAllActivities,
    viewAllActivityRows.length,
    viewAllBatchForecastError,
    viewAllBatchForecastLoading,
    viewAllBatchTotalsError,
    viewAllBatchTotalsLoading,
    viewAllMonthStarts.length,
  ]);

  const viewAllExportDisabledReason = useMemo(() => {
    if (!viewAllActivities) return 'Switch to View All to export all activity forecasts.';
    if (viewAllBatchForecastLoading || viewAllBatchTotalsLoading) return 'Loading activity forecast data…';
    if (viewAllBatchForecastError || viewAllBatchTotalsError) return 'Cannot export while View All data failed to load.';
    if (hasViewAllUnsavedChanges) return 'Save section changes before exporting View All.';
    if (viewAllActivityRows.length === 0 || viewAllMonthStarts.length === 0) return 'No View All data available for export.';
    return undefined;
  }, [
    hasViewAllUnsavedChanges,
    viewAllActivities,
    viewAllActivityRows.length,
    viewAllBatchForecastError,
    viewAllBatchForecastLoading,
    viewAllBatchTotalsError,
    viewAllBatchTotalsLoading,
    viewAllMonthStarts.length,
  ]);

  const canExportSingle = useMemo(() => {
    return (
      !viewAllActivities &&
      Number.isInteger(taskId) &&
      Number.isInteger(activityId) &&
      !forecastBusy &&
      !forecastError &&
      !hasUnsavedChanges &&
      monthStartsForTable.length > 0
    );
  }, [
    activityId,
    forecastBusy,
    forecastError,
    hasUnsavedChanges,
    monthStartsForTable.length,
    taskId,
    viewAllActivities,
  ]);

  const singleExportDisabledReason = useMemo(() => {
    if (viewAllActivities) return 'Switch to View Single to export the selected activity.';
    if (!Number.isInteger(taskId) || !Number.isInteger(activityId)) return 'Select a valid activity to export.';
    if (forecastBusy) return 'Loading forecast data…';
    if (forecastError) return 'Cannot export while forecast data failed to load.';
    if (hasUnsavedChanges) return 'Save changes before exporting.';
    if (monthStartsForTable.length === 0) return 'No forecast data available for export.';
    return undefined;
  }, [activityId, forecastBusy, forecastError, hasUnsavedChanges, monthStartsForTable.length, taskId, viewAllActivities]);

  const viewAllExportMonthOptions = useMemo(() => {
    const nowMonthStart = toMonthStartIso(new Date());
    const first = '2010-01-01';
    const last = addMonths(nowMonthStart, 120) || nowMonthStart;
    return buildMonthRangeInclusive(first, last, 360).map((m) => ({
      label: formatMonthLabel(m),
      value: m,
    }));
  }, []);

  const selectedViewAllExportMonths = useMemo(
    () => buildMonthRangeInclusive(viewAllExportStartMonth, viewAllExportEndMonth, 240),
    [viewAllExportEndMonth, viewAllExportStartMonth]
  );

  const selectedViewAllExportRangeLabel = useMemo(() => {
    if (selectedViewAllExportMonths.length === 0) return '';
    const first = formatMonthLabel(selectedViewAllExportMonths[0]);
    const last = formatMonthLabel(selectedViewAllExportMonths[selectedViewAllExportMonths.length - 1]);
    return first === last ? first : `${first} – ${last}`;
  }, [selectedViewAllExportMonths]);

  const selectedSingleExportMonths = useMemo(
    () => buildMonthRangeInclusive(singleExportStartMonth, singleExportEndMonth, 240),
    [singleExportEndMonth, singleExportStartMonth]
  );

  const selectedSingleExportRangeLabel = useMemo(() => {
    if (selectedSingleExportMonths.length === 0) return '';
    const first = formatMonthLabel(selectedSingleExportMonths[0]);
    const last = formatMonthLabel(selectedSingleExportMonths[selectedSingleExportMonths.length - 1]);
    return first === last ? first : `${first} – ${last}`;
  }, [selectedSingleExportMonths]);

  useEffect(() => {
    if (!viewAllExportModalOpen) return;
    const first = viewAllMonthStarts[0] || getCurrentYearStartMonthIso();
    const last = viewAllMonthStarts[viewAllMonthStarts.length - 1] || first;
    setViewAllExportStartMonth(first);
    setViewAllExportEndMonth(last);
    setViewAllExportError('');
  }, [viewAllExportModalOpen, viewAllMonthStarts]);

  useEffect(() => {
    if (!singleExportModalOpen) return;
    const first = monthStartsForTable[0] || getCurrentYearStartMonthIso();
    const last = monthStartsForTable[monthStartsForTable.length - 1] || first;
    setSingleExportStartMonth(first);
    setSingleExportEndMonth(last);
    setSingleExportError('');
  }, [monthStartsForTable, singleExportModalOpen]);

  const requestApiJson = useCallback(
    async (endpoint) => {
      const apiBase = resolveApiBaseUrl();
      const url = `${apiBase.replace(/\/+$/, '')}${endpoint}`;
      const response = await authenticatedFetch(url);
      const text = await response.text();
      const json = text
        ? (() => {
            try {
              return JSON.parse(text);
            } catch {
              return null;
            }
          })()
        : null;

      if (!response.ok) {
        throw new Error(json?.error || text || `Request failed (${response.status})`);
      }

      return json && typeof json === 'object' ? json : {};
    },
    [authenticatedFetch]
  );

  const exportViewAllForMonths = useCallback(async (monthStarts) => {
    if (!Number.isInteger(taskId) || !Array.isArray(monthStarts) || monthStarts.length === 0) {
      throw new Error('Please select a valid month range before exporting.');
    }

    const monthsKey = monthStarts.join(',');
    const viewAllForecastExport = await requestApiJson(
      `/tasks/${taskId}/activities/hour-forecast?months=${encodeURIComponent(monthsKey)}`
    );

    const exportActivities = Array.isArray(viewAllForecastExport?.activities) ? viewAllForecastExport.activities : [];
    if (exportActivities.length === 0) {
      throw new Error('No View All forecast data available for the selected range.');
    }

    const exportForecastByActivityId = new Map();
    for (const activity of exportActivities) {
      const activityId = Number(activity?.activityId);
      if (!Number.isInteger(activityId)) continue;
      exportForecastByActivityId.set(activityId, activity);
    }

    const activityMetaById = new Map();
    for (const row of viewAllActivityRows) {
      const activityId = Number(row?.activityId);
      if (!Number.isInteger(activityId)) continue;
      activityMetaById.set(activityId, row);
    }

    const monthColumns = monthStarts.map((ms) => ({
      header: formatMonthLabel(ms),
      key: `m:${ms}`,
      width: 11,
      numFmt: '0.00',
    }));

    const summaryColumns = [
      { header: 'Activity', key: 'activity', width: 44 },
      ...monthColumns,
      { header: 'All-time Total (h)', key: 'allTimeTotalHours', width: 16, numFmt: '0.00' },
      { header: 'All-time Cost ($)', key: 'allTimeCost', width: 16, numFmt: '$#,##0.00' },
    ];

    const detailColumns = [
      { header: 'Activity', key: 'activity', width: 44 },
      { header: 'Person', key: 'user', width: 26 },
      { header: 'Role', key: 'role', width: 10 },
      { header: 'Rate', key: 'rate', width: 28 },
      ...monthColumns,
      { header: 'All-time Total (h)', key: 'allTimeTotalHours', width: 16, numFmt: '0.00' },
      { header: 'All-time Cost ($)', key: 'allTimeCost', width: 16, numFmt: '$#,##0.00' },
    ];

    const summaryRows = [];
    const detailRows = [];
    const sheets = [];

    for (const [activityId, sectionForecast] of exportForecastByActivityId.entries()) {
      if (!Number.isInteger(activityId)) continue;

      const activityMeta = activityMetaById.get(activityId) || null;
      const sectionTotals = viewAllBatchTotalsByActivityId.get(activityId);

      const sectionRows = Array.isArray(sectionForecast?.rows) ? sectionForecast.rows : [];
      const sectionMonthTotals = {};
      for (const ms of monthStarts) {
        const fromApi = Number(sectionForecast?.totals?.[ms]);
        if (Number.isFinite(fromApi)) {
          sectionMonthTotals[ms] = fromApi;
          continue;
        }
        sectionMonthTotals[ms] = sectionRows.reduce((sum, row) => sum + Number(row?.hours?.[ms] ?? 0), 0);
      }

      const rangeTotalHours = monthStarts.reduce((sum, ms) => sum + Number(sectionMonthTotals[ms] ?? 0), 0);
      const rangeCost = calcWindowCostForRows(sectionRows, monthStarts);
      const allTimeTotalHours = Number.isFinite(Number(sectionTotals?.totalHours))
        ? Number(sectionTotals.totalHours)
        : Number(rangeTotalHours);
      const allTimeCost = Number.isFinite(Number(sectionTotals?.totalCost))
        ? Number(sectionTotals.totalCost)
        : Number(rangeCost);

      const activityName = String(activityMeta?.activityName || sectionForecast?.activityName || 'Activity');
      const activityNumber = Number(activityMeta?.activityNumber);
      const activityTitle = `${Number.isInteger(activityNumber) ? `#${activityNumber} ` : ''}${activityName}`;

      const summaryRow = {
        activity: activityTitle,
        allTimeTotalHours,
        allTimeCost,
      };
      for (const ms of monthStarts) summaryRow[`m:${ms}`] = Number(sectionMonthTotals[ms] ?? 0);
      summaryRows.push(summaryRow);

      const totalsByUserId = new Map(
        (Array.isArray(sectionTotals?.rows) ? sectionTotals.rows : [])
          .map((r) => {
            const uid = Number(r?.userId);
            if (!Number.isInteger(uid)) return null;
            return [uid, {
              totalHours: Number(r?.totalHours ?? 0),
              totalCost: Number(r?.totalCost ?? 0),
            }];
          })
          .filter(Boolean)
      );

      const activityColumns = [
        { header: 'Person', key: 'user', width: 26 },
        { header: 'Role', key: 'role', width: 10 },
        { header: 'Rate', key: 'rate', width: 28 },
        ...monthColumns,
        { header: 'All-time Total (h)', key: 'allTimeTotalHours', width: 16, numFmt: '0.00' },
        { header: 'All-time Cost ($)', key: 'allTimeCost', width: 16, numFmt: '$#,##0.00' },
      ];

      const sortedSectionRows = [...sectionRows].sort((a, b) => {
        const aLead = String(a?.role || '').toLowerCase() === 'lead' ? 0 : 1;
        const bLead = String(b?.role || '').toLowerCase() === 'lead' ? 0 : 1;
        if (aLead !== bLead) return aLead - bLead;
        return String(a?.userName || '').localeCompare(String(b?.userName || ''));
      });

      const activitySheetRows = sortedSectionRows.map((row, rowIndex) => {
        const uid = Number(row?.userId);
        const role = String(row?.role || '').toLowerCase() === 'lead' ? 'Lead' : 'Support';
        const byUserTotals = Number.isInteger(uid) ? totalsByUserId.get(uid) : null;
        const rowRangeTotalHours = monthStarts.reduce((sum, ms) => sum + Number(row?.hours?.[ms] ?? 0), 0);
        const rateSegments = buildRateSegmentsWithEffectiveDate(
          monthStarts,
          (ms) => {
            const monthRate = Number(row?.rates?.[ms]);
            if (Number.isFinite(monthRate)) return monthRate;
            const fallbackRate = Number(row?.rate);
            return Number.isFinite(fallbackRate) ? fallbackRate : null;
          },
          (ms) => {
            const s = String(row?.rateEffectiveDates?.[ms] ?? '').slice(0, 10);
            return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
          }
        );

        const rateLabel = rateSegments.length > 0
          ? rateSegments
              .map((seg) => `${formatRate(seg.rate)} (Eff: ${seg.effectiveDate || '—'})`)
              .join(' / ')
          : '—';

        const out = {
          user: String(row?.userName || '—'),
          role,
          rate: rateLabel,
          allTimeTotalHours: Number(byUserTotals?.totalHours ?? 0),
          allTimeCost: Number(byUserTotals?.totalCost ?? 0),
        };

        for (const ms of monthStarts) {
          out[`m:${ms}`] = Number(row?.hours?.[ms] ?? 0);
        }

        const rowRangeCost = monthStarts.reduce((sum, ms) => {
          const hours = Number(row?.hours?.[ms] ?? 0);
          const rate = Number(row?.rates?.[ms] ?? row?.rate);
          if (!Number.isFinite(hours) || !Number.isFinite(rate)) return sum;
          return sum + hours * rate;
        }, 0);

        if (!Number.isFinite(Number(out.allTimeTotalHours)) || Number(out.allTimeTotalHours) === 0) {
          out.allTimeTotalHours = Number(rowRangeTotalHours ?? 0);
        }
        if (!Number.isFinite(Number(out.allTimeCost))) {
          out.allTimeCost = Number(rowRangeCost ?? 0);
        }

        detailRows.push({
          activity: rowIndex === 0 ? activityTitle : '',
          ...out,
        });

        return out;
      });

      const totalRow = {
        user: 'Total',
        role: '',
        rate: null,
        allTimeTotalHours,
        allTimeCost,
      };
      for (const ms of monthStarts) totalRow[`m:${ms}`] = Number(sectionMonthTotals[ms] ?? 0);
      activitySheetRows.push(totalRow);

      detailRows.push({
        activity: '',
        ...totalRow,
      });

      sheets.push({
        name: activityTitle,
        columns: activityColumns,
        rows: activitySheetRows,
      });
    }

    const summaryTotals = summaryRows.reduce(
      (acc, row) => {
        acc.allTimeTotalHours += Number(row?.allTimeTotalHours ?? 0);
        acc.allTimeCost += Number(row?.allTimeCost ?? 0);
        for (const ms of monthStarts) acc[`m:${ms}`] += Number(row?.[`m:${ms}`] ?? 0);
        return acc;
      },
      monthStarts.reduce(
        (acc, ms) => {
          acc[`m:${ms}`] = 0;
          return acc;
        },
        { activity: 'Total', allTimeTotalHours: 0, allTimeCost: 0 }
      )
    );
    summaryRows.push(summaryTotals);

    detailRows.push({
      activity: 'All Activities',
      user: 'Grand Total',
      role: '',
      rate: null,
      ...monthStarts.reduce((acc, ms) => {
        acc[`m:${ms}`] = Number(summaryTotals?.[`m:${ms}`] ?? 0);
        return acc;
      }, {}),
      allTimeTotalHours: Number(summaryTotals?.allTimeTotalHours ?? 0),
      allTimeCost: Number(summaryTotals?.allTimeCost ?? 0),
    });

    const safeTaskName = String(task?.name || task?.taskName || `Task ${taskId || ''}`)
      .replace(/[\\/:*?"<>|]/g, ' ')
      .trim() || 'Task';
    const firstMonthLabel = formatMonthLabel(monthStarts[0]);
    const lastMonthLabel = formatMonthLabel(monthStarts[monthStarts.length - 1]);
    const exportRangeLabel = firstMonthLabel === lastMonthLabel ? firstMonthLabel : `${firstMonthLabel} – ${lastMonthLabel}`;
    const safeRangeLabel = String(exportRangeLabel || 'Range').replace(/[\\/:*?"<>|]/g, ' ').trim() || 'Range';

    await exportSheetsToXlsx({
      fileName: `${safeTaskName} - View All Forecast - ${safeRangeLabel}.xlsx`,
      sheets: [
        { name: 'All Activity People', columns: detailColumns, rows: detailRows },
        { name: 'Activity Totals', columns: summaryColumns, rows: summaryRows },
        ...sheets,
      ],
    });
  }, [
    requestApiJson,
    task?.name,
    task?.taskName,
    taskId,
    viewAllActivityRows,
    viewAllBatchTotalsByActivityId,
  ]);

  const exportSingleForMonths = useCallback(async (monthStarts) => {
    if (!Number.isInteger(taskId) || !Number.isInteger(activityId) || !Array.isArray(monthStarts) || monthStarts.length === 0) {
      throw new Error('Please select a valid month range before exporting.');
    }

    const monthsKey = monthStarts.join(',');
    const [activityForecastExport, activityTotalsExport] = await Promise.all([
      requestApiJson(`/tasks/${taskId}/activities/${activityId}/hour-forecast?months=${encodeURIComponent(monthsKey)}`),
      requestApiJson(`/tasks/${taskId}/activities/${activityId}/hour-forecast-totals`),
    ]);

    const sectionRows = Array.isArray(activityForecastExport?.rows) ? activityForecastExport.rows : [];
    if (sectionRows.length === 0) {
      throw new Error('No activity forecast data available for the selected range.');
    }

    const sectionMonthTotals = {};
    for (const ms of monthStarts) {
      const fromApi = Number(activityForecastExport?.totals?.[ms]);
      if (Number.isFinite(fromApi)) {
        sectionMonthTotals[ms] = fromApi;
        continue;
      }
      sectionMonthTotals[ms] = sectionRows.reduce((sum, row) => sum + Number(row?.hours?.[ms] ?? 0), 0);
    }

    const rangeTotalHours = monthStarts.reduce((sum, ms) => sum + Number(sectionMonthTotals[ms] ?? 0), 0);
    const rangeCost = calcWindowCostForRows(sectionRows, monthStarts);
    const allTimeTotalHours = Number.isFinite(Number(activityTotalsExport?.totalHours))
      ? Number(activityTotalsExport.totalHours)
      : Number(rangeTotalHours);
    const allTimeCost = Number.isFinite(Number(activityTotalsExport?.totalCost))
      ? Number(activityTotalsExport.totalCost)
      : Number(rangeCost);

    const totalsByUserId = new Map(
      (Array.isArray(activityTotalsExport?.rows) ? activityTotalsExport.rows : [])
        .map((r) => {
          const uid = Number(r?.userId);
          if (!Number.isInteger(uid)) return null;
          return [uid, {
            totalHours: Number(r?.totalHours ?? 0),
            totalCost: Number(r?.totalCost ?? 0),
          }];
        })
        .filter(Boolean)
    );

    const monthColumns = monthStarts.map((ms) => ({
      header: formatMonthLabel(ms),
      key: `m:${ms}`,
      width: 11,
      numFmt: '0.00',
    }));

    const detailColumns = [
      { header: 'Person', key: 'user', width: 26 },
      { header: 'Role', key: 'role', width: 10 },
      { header: 'Rate', key: 'rate', width: 28 },
      ...monthColumns,
      { header: 'All-time Total (h)', key: 'allTimeTotalHours', width: 16, numFmt: '0.00' },
      { header: 'All-time Cost ($)', key: 'allTimeCost', width: 16, numFmt: '$#,##0.00' },
    ];

    const summaryColumns = [
      ...monthColumns,
      { header: 'All-time Total (h)', key: 'allTimeTotalHours', width: 16, numFmt: '0.00' },
      { header: 'All-time Cost ($)', key: 'allTimeCost', width: 16, numFmt: '$#,##0.00' },
    ];

    const sortedSectionRows = [...sectionRows].sort((a, b) => {
      const aLead = String(a?.role || '').toLowerCase() === 'lead' ? 0 : 1;
      const bLead = String(b?.role || '').toLowerCase() === 'lead' ? 0 : 1;
      if (aLead !== bLead) return aLead - bLead;
      return String(a?.userName || '').localeCompare(String(b?.userName || ''));
    });

    const detailRows = sortedSectionRows.map((row) => {
      const uid = Number(row?.userId);
      const role = String(row?.role || '').toLowerCase() === 'lead' ? 'Lead' : 'Support';
      const byUserTotals = Number.isInteger(uid) ? totalsByUserId.get(uid) : null;
      const rowRangeTotalHours = monthStarts.reduce((sum, ms) => sum + Number(row?.hours?.[ms] ?? 0), 0);
      const rateSegments = buildRateSegmentsWithEffectiveDate(
        monthStarts,
        (ms) => {
          const monthRate = Number(row?.rates?.[ms]);
          if (Number.isFinite(monthRate)) return monthRate;
          const fallbackRate = Number(row?.rate);
          return Number.isFinite(fallbackRate) ? fallbackRate : null;
        },
        (ms) => {
          const s = String(row?.rateEffectiveDates?.[ms] ?? '').slice(0, 10);
          return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
        }
      );

      const rateLabel = rateSegments.length > 0
        ? rateSegments
            .map((seg) => `${formatRate(seg.rate)} (Eff: ${seg.effectiveDate || '—'})`)
            .join(' / ')
        : '—';

      const out = {
        user: String(row?.userName || '—'),
        role,
        rate: rateLabel,
        allTimeTotalHours: Number(byUserTotals?.totalHours ?? 0),
        allTimeCost: Number(byUserTotals?.totalCost ?? 0),
      };

      for (const ms of monthStarts) {
        out[`m:${ms}`] = Number(row?.hours?.[ms] ?? 0);
      }

      const rowRangeCost = monthStarts.reduce((sum, ms) => {
        const hours = Number(row?.hours?.[ms] ?? 0);
        const rate = Number(row?.rates?.[ms] ?? row?.rate);
        if (!Number.isFinite(hours) || !Number.isFinite(rate)) return sum;
        return sum + hours * rate;
      }, 0);

      if (!Number.isFinite(Number(out.allTimeTotalHours)) || Number(out.allTimeTotalHours) === 0) {
        out.allTimeTotalHours = Number(rowRangeTotalHours ?? 0);
      }
      if (!Number.isFinite(Number(out.allTimeCost))) {
        out.allTimeCost = Number(rowRangeCost ?? 0);
      }

      return out;
    });

    const totalRow = {
      user: 'Total',
      role: '',
      rate: null,
      allTimeTotalHours,
      allTimeCost,
    };
    for (const ms of monthStarts) totalRow[`m:${ms}`] = Number(sectionMonthTotals[ms] ?? 0);
    detailRows.push(totalRow);

    const summaryRows = [
      {
        ...monthStarts.reduce((acc, ms) => {
          acc[`m:${ms}`] = Number(sectionMonthTotals[ms] ?? 0);
          return acc;
        }, {}),
        allTimeTotalHours,
        allTimeCost,
      },
    ];

    const activityLabel = String(activityForecastExport?.activityName || `Activity ${activityId}`);
    const safeTaskName = String(task?.name || task?.taskName || `Task ${taskId || ''}`)
      .replace(/[\\/:*?"<>|]/g, ' ')
      .trim() || 'Task';
    const safeActivityLabel = String(activityLabel || 'Activity')
      .replace(/[\\/:*?"<>|]/g, ' ')
      .trim() || 'Activity';
    const firstMonthLabel = formatMonthLabel(monthStarts[0]);
    const lastMonthLabel = formatMonthLabel(monthStarts[monthStarts.length - 1]);
    const exportRangeLabel = firstMonthLabel === lastMonthLabel ? firstMonthLabel : `${firstMonthLabel} – ${lastMonthLabel}`;
    const safeRangeLabel = String(exportRangeLabel || 'Range').replace(/[\\/:*?"<>|]/g, ' ').trim() || 'Range';

    await exportSheetsToXlsx({
      fileName: `${safeTaskName} - ${safeActivityLabel} - Forecast - ${safeRangeLabel}.xlsx`,
      sheets: [
        { name: 'Activity People', columns: detailColumns, rows: detailRows },
        { name: 'Activity Totals', columns: summaryColumns, rows: summaryRows },
      ],
    });
  }, [activityId, requestApiJson, task?.name, task?.taskName, taskId]);

  const handleExportViewAllXlsx = useCallback(() => {
    if (!canExportViewAll) return;
    setViewAllExportModalOpen(true);
  }, [canExportViewAll]);

  const handleExportSingleXlsx = useCallback(() => {
    if (!canExportSingle) return;
    setSingleExportModalOpen(true);
  }, [canExportSingle]);

  const handleViewAllExportConfirm = useCallback(async () => {
    if (selectedViewAllExportMonths.length === 0) {
      setViewAllExportError('Choose a valid month range before exporting.');
      return;
    }

    setViewAllExporting(true);
    setViewAllExportError('');

    try {
      await exportViewAllForMonths(selectedViewAllExportMonths);
      setViewAllExportModalOpen(false);
    } catch (err) {
      setViewAllExportError(String(err?.message || err || 'Failed to export forecast.'));
    } finally {
      setViewAllExporting(false);
    }
  }, [exportViewAllForMonths, selectedViewAllExportMonths]);

  const handleSingleExportConfirm = useCallback(async () => {
    if (selectedSingleExportMonths.length === 0) {
      setSingleExportError('Choose a valid month range before exporting.');
      return;
    }

    setSingleExporting(true);
    setSingleExportError('');

    try {
      await exportSingleForMonths(selectedSingleExportMonths);
      setSingleExportModalOpen(false);
    } catch (err) {
      setSingleExportError(String(err?.message || err || 'Failed to export forecast.'));
    } finally {
      setSingleExporting(false);
    }
  }, [exportSingleForMonths, selectedSingleExportMonths]);

  const allocatedTaskBudget = useMemo(() => {
    const raw =
      task?.budgetTarget ??
      task?.budget_target ??
      task?.allocatedBudget ??
      task?.allocated_budget ??
      null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [task?.allocatedBudget, task?.allocated_budget, task?.budgetTarget, task?.budget_target]);

  // Budget is allocated at the discipline level, so roll every activity up regardless of which one is being edited.
  // In View All the section totals already reflect live edits; in single-activity view the open activity uses its
  // own totals and the rest fall back to the project summary.
  const disciplineForecastTotals = useMemo(() => {
    if (viewAllActivities) {
      return {
        hours: Number(viewAllTotals?.totalHours ?? 0),
        cost: Number(viewAllTotals?.totalCost ?? 0),
      };
    }

    return viewAllActivityRows.reduce(
      (acc, row) => {
        const aId = Number(row?.activityId);
        const isSelected = Number.isInteger(aId) && Number.isInteger(activityId) && aId === Number(activityId);

        if (isSelected && allTimeTotals) {
          acc.hours += Number(allTimeTotals?.totalHours ?? 0);
          acc.cost += Number(allTimeTotals?.totalCost ?? 0);
        } else {
          acc.hours += Number(row?.totalHours ?? 0);
          acc.cost += Number(row?.totalCost ?? 0);
        }

        return acc;
      },
      { hours: 0, cost: 0 }
    );
  }, [activityId, allTimeTotals, viewAllActivities, viewAllActivityRows, viewAllTotals]);

  // Allocated budget is a dollar figure, so forecast hours are compared at their projected cost.
  const disciplineBudgetStatus = useMemo(() => {
    if (allocatedTaskBudget === null || !(allocatedTaskBudget > 0)) return null;
    if (activitySummaryLoading || activitySummaryError) return null;
    if (!viewAllActivities && viewAllActivityRows.length === 0) return null;

    const cost = Number(disciplineForecastTotals?.cost ?? 0);
    if (!Number.isFinite(cost)) return null;

    const delta = cost - allocatedTaskBudget;
    return {
      cost,
      hours: Number(disciplineForecastTotals?.hours ?? 0),
      delta,
      isOver: delta > 0,
      percent: (Math.abs(delta) / allocatedTaskBudget) * 100,
    };
  }, [
    activitySummaryError,
    activitySummaryLoading,
    allocatedTaskBudget,
    disciplineForecastTotals,
    viewAllActivities,
    viewAllActivityRows.length,
  ]);

  const disciplineBudgetUsagePercent = useMemo(() => {
    if (!disciplineBudgetStatus) return null;
    if (!Number.isFinite(Number(allocatedTaskBudget)) || Number(allocatedTaskBudget) <= 0) return null;
    const pct = (Number(disciplineBudgetStatus.cost) / Number(allocatedTaskBudget)) * 100;
    if (!Number.isFinite(pct)) return null;
    return Math.max(0, pct);
  }, [allocatedTaskBudget, disciplineBudgetStatus]);

  const selectedActivity = useMemo(() => {
    if (!selectedValue) return null;
    return activityOptions.find((o) => String(o?.value) === String(selectedValue)) ?? null;
  }, [activityOptions, selectedValue]);

  const selectedActivityLabel = useMemo(() => {
    if (!selectedActivity) return '';
    const n = Number(selectedActivity?.activityNumber);
    const num = Number.isInteger(n) ? `#${n} ` : '';
    return `${num}${selectedActivity?.label ?? ''}`.trim();
  }, [selectedActivity]);

  const requestDiscardConfirmation = useCallback((onConfirm, mode = 'single') => {
    const hasChanges = mode === 'view-all' ? hasViewAllUnsavedChanges : hasUnsavedChanges;
    if (!hasChanges) {
      if (typeof onConfirm === 'function') onConfirm();
      return true;
    }

    pendingDiscardActionRef.current = typeof onConfirm === 'function' ? onConfirm : null;
    setDiscardConfirmMode(mode);
    setDiscardConfirmOpen(true);
    return false;
  }, [hasUnsavedChanges, hasViewAllUnsavedChanges]);

  const closeDiscardConfirm = useCallback(() => {
    pendingDiscardActionRef.current = null;
    setDiscardConfirmOpen(false);
  }, []);

  const confirmDiscardWithModal = useCallback(() => {
    const pending = pendingDiscardActionRef.current;
    pendingDiscardActionRef.current = null;
    setDiscardConfirmOpen(false);
    if (discardConfirmMode === 'single') discardAllDrafts();
    if (typeof pending === 'function') pending();
  }, [discardConfirmMode, discardAllDrafts]);

  const toggleViewAllActivities = () => {
    if (!requestDiscardConfirmation(() => {
      if (editingSupports) exitSupportEditMode();
      startViewToggleTransition(() => {
        setViewAllActivities((prev) => {
          const next = !prev;
          // View All uses one shared contiguous month bucket for every activity section.
          if (next && filterMonthsWithHours) setFilterMonthsWithHours(false);
          if (!next) {
            // Clear aggregates in one shot when leaving View All.
            setViewAllSectionTotalsByActivityId({});
            setViewAllSectionUnsavedByActivityId({});
          }
          return next;
        });
      });
    }, viewAllActivities ? 'view-all' : 'single')) return;
  };

  useEffect(() => {
    if (!viewAllActivities || !hasViewAllUnsavedChanges) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasViewAllUnsavedChanges, viewAllActivities]);

  useEffect(() => {
    if (!printMode) return undefined;

    const handleAfterPrint = () => setPrintMode(false);
    window.addEventListener('afterprint', handleAfterPrint);

    // Ensure the print layout is rendered before printing.
    const t = setTimeout(() => {
      try {
        window.print();
      } catch {
        setPrintMode(false);
      }
    }, 50);

    return () => {
      clearTimeout(t);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [printMode]);

  const renderMonthHeader = (ms) => {
    const hours = workableHoursByMonthStart.get(ms);
    const prefix = Number.isFinite(hours) ? `(${Math.round(hours)} hrs) - ` : '';
    const monthCloseDates = closeDatesByMonthStart.get(ms) ?? [];
    const tooltipText =
      monthCloseDates.length > 0
        ? monthCloseDates
            .map((cd) => (cd.description ? `${cd.label} — ${cd.description}` : cd.label))
            .join('\n')
        : 'No close dates.';
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, whiteSpace: 'nowrap' }}>
        {prefix ? (
          <Whisper
            trigger="hover"
            placement="top"
            enterable
            speaker={
              <Tooltip>
                <div style={{ whiteSpace: 'pre-line', maxWidth: 360 }}>{tooltipText}</div>
              </Tooltip>
            }
          >
            <span style={{ fontWeight: 700, color: '#666', cursor: 'help' }}>{prefix}</span>
          </Whisper>
        ) : (
          <span style={{ fontWeight: 700, color: '#666' }}>{prefix}</span>
        )}
        <span>{formatMonthLabel(ms)}</span>
      </div>
    );
  };

  const renderMonthCell = (rowData, ms) => {
    const isTotal = isTotalRow(rowData);

    if (isTotal) {
      const totalHours = Number(rowData?.hours?.[ms] ?? 0);
      const totalCost = Number(rowData?.costs?.[ms] ?? 0);
      return (
        <div
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-end',
            gap: 2,
          }}
        >
          <div style={{ fontWeight: 800 }}>{totalHours.toFixed(2)}h</div>
          <div style={{ fontWeight: 800, fontSize: 12 }}>{formatCurrency(totalCost)}</div>
        </div>
      );
    }

    const value = getDisplayHours(rowData, ms);
    const monthLocked = isPastMonthLocked(ms);
    const cost = formatCost(getRateForMonth(rowData, ms), value);
    const flashFillRun = flashFillRunsByUserId.get(rowData?.userId)?.get(ms);
    const flashFillRunKey = flashFillRun ? getFlashFillRunKey(rowData.userId, flashFillRun) : undefined;
    const workingHours = workableHoursByMonthStart.get(ms);
    const ftePercent = Number(userFteById.get(Number(rowData?.userId)) ?? 100);
    const fteScale = Number.isFinite(ftePercent) ? Math.max(0, ftePercent) / 100 : 1;
    const timeOffHours = Number(timeOffHoursByUserMonth.get(`${rowData?.userId}|${ms}`) ?? 0);
    const monthCapacity = getMonthCapacity(workingHours, timeOffHours, fteScale);
    const committedPct = getUtilizationPercent(rowData?.committedHours?.[ms] ?? 0, monthCapacity);
    const committedLabel =
      Number.isInteger(committedPct) ? `${committedPct}% util` : null;

    return (
      <div
        data-flash-fill-run={flashFillRunKey}
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-end',
          gap: 2,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
          {flashFillRun && flashFillRun.months[0] === ms ? (
            canForecast ? (
              <FlashFillButton
                runKey={flashFillRunKey}
                run={flashFillRun}
                disabled={savingAll}
                onAccept={() => applyFlashFill(rowData, [flashFillRun])}
              />
            ) : null
          ) : null}
          {(() => {
            const inputNode = (
              <InputNumber
                size="sm"
                min={0}
                step={0.25}
                precision={2}
                value={value}
                onFocus={(e) => {
                  // Select-all so typing replaces the whole value (prevents leading 0 sticking around).
                  queueMicrotask(() => e?.target?.select?.());
                }}
                onClick={(e) => {
                  // Clicking into the input should also select-all.
                  queueMicrotask(() => e?.target?.select?.());
                }}
                onChange={(v) => onChangeCell(rowData, ms, v)}
                onBlur={() => commitCell(rowData, ms)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitCell(rowData, ms);
                  } else if (isFlashFillShortcut(e)) {
                    e.preventDefault();
                    applyFlashFill(rowData, uniqueFlashFillRuns(flashFillRunsByUserId.get(rowData?.userId)));
                  }
                }}
                disabled={savingAll || monthLocked || !canForecast}
                style={{ width: 120 }}
              />
            );

            if (!monthLocked) {
              if (!flashFillRun) return inputNode;
              return <FlashFillGhost text={flashFillRun.value.toFixed(2)}>{inputNode}</FlashFillGhost>;
            }

            return (
              <Whisper
                trigger="hover"
                placement="top"
                speaker={<Tooltip>Cannot edit past forecasts</Tooltip>}
              >
                <span style={{ position: 'relative', display: 'inline-flex', width: 120 }}>
                  {inputNode}
                  <button
                    type="button"
                    aria-label="Cannot edit past forecasts"
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      registerPastForecastUnlockAttempt();
                    }}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      margin: 0,
                      cursor: 'not-allowed',
                    }}
                  />
                </span>
              </Whisper>
            );
          })()}
        </div>
        <div
          style={{
            fontSize: 12,
            color: '#666',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          {committedLabel ? <span>{committedLabel}</span> : null}
          <span className={flashFillRun ? 'ppc-flash-fill-preview-hide' : undefined}>{cost}</span>
          {flashFillRun ? (
            <span className="ppc-flash-fill-preview-only">
              {formatCost(getRateForMonth(rowData, ms), flashFillRun.value)}
            </span>
          ) : null}
        </div>
      </div>
    );
  };

  // Off-screen copy of a month's header or cell: inert so it can't take focus or clicks until the
  // slide brings it into view and the real column replaces it.
  const renderSlideInMonth = (ms, render) => (ms ? (
    <div className="ppc-forecast-month-ghost__content" inert>
      {render(ms)}
    </div>
  ) : null);

  return (
    <div className="ppc-page ppc-forecast-page">
      <div className="ppc-noprint">
        <div
          ref={setPageHeaderEl}
          className={`ppc-forecast-page-header${viewAllActivities ? ' ppc-forecast-page-header--sticky' : ''}`}
        >
          <div className="ppc-forecast-page-header__left">
            <div className="ppc-actuals-crumbs" aria-label="Breadcrumb">
              <button
                type="button"
                className="ppc-actuals-crumb-link"
                onClick={() => navigate('/')}
              >
                Projects
              </button>
              <i className="bi bi-chevron-right ppc-actuals-crumb-sep" aria-hidden="true" />
              <button
                type="button"
                className="ppc-actuals-crumb-link"
                onClick={() => {
                  if (Number.isInteger(projectLookupId)) {
                    navigate(`/projects/${projectLookupId}`);
                    return;
                  }
                  navigate('/');
                }}
              >
                {project?.name || 'Project'}
              </button>
              <i className="bi bi-chevron-right ppc-actuals-crumb-sep" aria-hidden="true" />
              {Number.isInteger(taskOrderId) ? (
                <button
                  type="button"
                  className="ppc-actuals-crumb-link"
                  onClick={() => {
                    if (taskOrderPath) navigate(taskOrderPath);
                  }}
                >
                  {selectedTaskOrder?.name || `Task Order ${taskOrderId}`}
                </button>
              ) : (
                <span className="ppc-actuals-crumb-link">Task Order</span>
              )}
              <i className="bi bi-chevron-right ppc-actuals-crumb-sep" aria-hidden="true" />
              <button
                type="button"
                className="ppc-actuals-crumb-link"
                onClick={() => {
                  if (disciplineDetailPath) navigate(disciplineDetailPath);
                }}
                disabled={!disciplineDetailPath}
              >
                {task?.task || task?.name || 'Discipline'}
              </button>
              <i className="bi bi-chevron-right ppc-actuals-crumb-sep" aria-hidden="true" />
              <span className="ppc-actuals-crumb-current">Forecast</span>
            </div>

            <h2 className="ppc-forecast-page-title">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                <i className="bi bi-clock" aria-hidden="true" />
                Forecast Hours
              </span>
            </h2>
          </div>

          <div className="ppc-forecast-page-header__actions">
            {hasUnsavedChanges && canForecast ? (
              <Button className="ppc-actuals-btn" appearance="primary" loading={savingAll} disabled={savingAll} onClick={saveAll}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontWeight: 600 }}>Save</span>
                  <i className="bi bi-floppy" aria-hidden="true" />
                </span>
              </Button>
            ) : null}
            <button
              type="button"
              className="ppc-actuals-btn"
              onClick={() => {
                requestDiscardConfirmation(() => {
                  const backFromActuals =
                    location?.state?.fromActuals === true && typeof location?.state?.actualsPath === 'string'
                      ? String(location.state.actualsPath)
                      : null;
                  if (backFromActuals) {
                    navigate(backFromActuals);
                    return;
                  }

                  const pid = Number.isInteger(projectIdParam)
                    ? projectIdParam
                    : Number.isInteger(Number(task?.projectId))
                      ? Number(task.projectId)
                      : null;
                  const toid = Number.isInteger(taskOrderIdParam)
                    ? taskOrderIdParam
                    : Number.isInteger(Number(task?.taskOrderId))
                      ? Number(task.taskOrderId)
                      : null;

                  if (Number.isInteger(pid) && Number.isInteger(toid) && Number.isInteger(taskId)) {
                    navigate(`/projects/${pid}/task-orders/${toid}/tasks/${taskId}`);
                    return;
                  }
                  if (Number.isInteger(taskId)) navigate(`/tasks/${taskId}`);
                  else navigate('/');
                }, viewAllActivities ? 'view-all' : 'single');
              }}
            >
              <i className="bi bi-arrow-left" aria-hidden="true" />
              Back
            </button>
          </div>
        </div>

        <Divider style={{ margin: '6px 0 14px' }} />

        {forecastError ? (
          <Message type="error" showIcon>
            Failed to load hour forecast.
          </Message>
        ) : null}

        {usersError ? (
          <Message type="warning" showIcon>
            % committed is using 0% reserved hours (failed to load users).
          </Message>
        ) : null}

        {timeOffError ? (
          <Message type="warning" showIcon>
            % committed is not counting time off (failed to load time off entries).
          </Message>
        ) : null}

        {activitySummaryError ? (
          <Message type="warning" showIcon>
            Failed to load activity summary totals.
          </Message>
        ) : null}

        {/* Flat section, not a card. The grid below already carries its own border,
            so a bordered Panel around it was a second box drawn over the first; the
            controls stay as the head band that sits above the table elsewhere. */}
        <div className="ppc-forecast-main">
          <div
            className={`ppc-forecast-controls-band${viewAllActivities ? ' ppc-forecast-controls-band--sticky' : ''}`}
            style={viewAllActivities ? { top: pageHeaderH } : undefined}
          >
            <Stack className="ppc-forecast-controls" justifyContent="space-between" alignItems="center" wrap>
              <div className="ppc-activity-picker-row">
                {!viewAllActivities ? (
                  <SelectPicker
                    className="ppc-activity-picker"
                    searchable
                    cleanable={false}
                    data={activityOptions}
                    value={selectedValue}
                    placeholder="Select activity"
                    renderValue={(value, item) => {
                      const n = Number(item?.activityNumber);
                      const num = Number.isInteger(n) ? `#${n} ` : '';
                      // Must be a string; returning a ReactNode can show up as "[object Object]" while searching.
                      const full = `Activity: ${num}${item?.label ?? ''}`;
                      return truncateText(full, 80);
                    }}
                    style={{ width: '100%', minWidth: 200, maxWidth: ACTIVITY_TOGGLE_WIDTH }}
                    popupClassName="ppc-activity-picker-popup"
                    popupStyle={{
                      width: ACTIVITY_TOGGLE_WIDTH,
                      maxWidth: 'min(680px, calc(100vw - 40px))',
                    }}
                    renderOption={(label, item) => {
                      const n = Number(item?.activityNumber);
                      const num = Number.isInteger(n) ? `#${n} ` : '';
                      return (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            padding: '2px 0',
                            width: '100%',
                            maxWidth: '100%',
                            overflow: 'hidden',
                          }}
                        >
                          <div className="ppc-activity-option-title" title={`${num}${label}`}>
                            {num}
                            {label}
                          </div>
                          <div style={{ fontSize: 12, color: '#666', whiteSpace: 'normal' }}>{String(item?.description || '')}</div>
                        </div>
                      );
                    }}
                    onChange={(v) => {
                      requestDiscardConfirmation(() => {
                        const raw = String(v || '');
                        const parts = raw.split('|');
                        const nextTaskId = Number(parts[0]);
                        const nextActivityId = Number(parts[1]);
                        if (Number.isInteger(nextTaskId) && Number.isInteger(nextActivityId)) {
                          navigate(
                            `/tasks/${nextTaskId}/activities/${nextActivityId}/forecast?start=${encodeURIComponent(
                              String(start || '').slice(0, 10)
                            )}`,
                            { state: location?.state }
                          );
                        }
                      }, 'single');
                    }}
                  />
                ) : null}
                <button
                  type="button"
                  className={`ppc-actuals-btn ppc-forecast-btn--sm${viewAllActivities ? ' ppc-forecast-btn--primary' : ''}`}
                  disabled={isViewTogglePending}
                  onClick={toggleViewAllActivities}
                >
                  {isViewTogglePending ? 'Switching…' : (viewAllActivities ? 'View Single' : 'View All')}
                </button>
                {!viewAllActivities ? <ForecastLastSaved forecast={forecast} /> : null}
              </div>

              <div className="ppc-forecast-range">
                <div className="ppc-forecast-range-row">
                  {!viewAllActivities ? (
                    <Checkbox
                      className="ppc-forecast-filter"
                      checked={filterMonthsWithHours}
                      onChange={(_, checked) => {
                        const next = !!checked;
                        if (next === filterMonthsWithHours) return;
                        requestDiscardConfirmation(() => {
                          setFilterMonthsWithHours(next);
                        }, 'single');
                      }}
                      style={{ fontSize: 12, color: 'var(--rs-text-secondary, #444)' }}
                    >
                      Filter for months with hours
                    </Checkbox>
                  ) : null}
                  {prevRangeBlocked ? (
                    <Whisper
                      trigger="hover"
                      placement="top"
                      speaker={<Tooltip>Already at the start of the project</Tooltip>}
                    >
                      <span style={{ display: 'inline-flex' }}>
                        <IconButton
                          appearance="subtle"
                          size="sm"
                          icon={<i className="bi bi-chevron-left" aria-hidden="true" />}
                          aria-label="Previous month"
                          title="Previous month"
                          disabled
                        />
                      </span>
                    </Whisper>
                  ) : (
                    <IconButton
                      appearance="subtle"
                      size="sm"
                      icon={<i className="bi bi-chevron-left" aria-hidden="true" />}
                      aria-label="Previous month"
                      title="Previous month"
                      onClick={goPrevMonths}
                    />
                  )}
                  <div className="ppc-forecast-range-label">{rangeLabel}</div>
                  {nextRangeBlocked ? (
                    <Whisper
                      trigger="hover"
                      placement="top"
                      speaker={<Tooltip>Already at the end of the project</Tooltip>}
                    >
                      <span style={{ display: 'inline-flex' }}>
                        <IconButton
                          appearance="subtle"
                          size="sm"
                          icon={<i className="bi bi-chevron-right" aria-hidden="true" />}
                          aria-label="Next month"
                          title="Next month"
                          disabled
                        />
                      </span>
                    </Whisper>
                  ) : (
                    <IconButton
                      appearance="subtle"
                      size="sm"
                      icon={<i className="bi bi-chevron-right" aria-hidden="true" />}
                      aria-label="Next month"
                      title="Next month"
                      onClick={goNextMonths}
                    />
                  )}
                  <button
                    type="button"
                    className="ppc-actuals-btn ppc-forecast-btn--sm"
                    onClick={goCurrentMonths}
                    disabled={isOnCurrentWindow}
                  >
                    Current
                  </button>
                </div>
              </div>
            </Stack>
          </div>
        {viewAllActivities ? (
          <div className="ppc-forecast-view-all-wrap">
            {activitySummaryLoading ? (
              <Message type="info" showIcon>
                Loading activity totals...
              </Message>
            ) : null}

            {!activitySummaryLoading && viewAllActivityRows.length === 0 ? (
              <Message type="info" showIcon>
                No activities found for this task.
              </Message>
            ) : null}

            {!activitySummaryLoading && viewAllActivityRows.length > 0 ? (
              <div className="ppc-forecast-view-all-list">
                {viewAllBatchForecastError || viewAllBatchTotalsError ? (
                  <Message type="error" showIcon style={{ marginBottom: 10 }}>
                    Failed to load some View All forecast data.
                  </Message>
                ) : null}

                {viewAllSectionCards}
              </div>
            ) : null}

          </div>
        ) : null}

        {!viewAllActivities && filterMonthsWithHours && !monthsWithHoursLoading && monthsWithHours.length === 0 ? (
          <Message type="info" showIcon style={{ marginBottom: 10 }}>
            No months have forecast hours yet.
          </Message>
        ) : null}

        {!viewAllActivities && editingSupports ? (
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 12, color: '#666', fontWeight: 700 }}>Edit supports</div>
              <SelectPicker
                searchable
                cleanable
                size="sm"
                placeholder={
                  usersLoading
                    ? 'Loading users…'
                    : addSupportOptions.length === 0
                      ? 'No more users to add'
                      : 'Add support…'
                }
                data={addSupportOptions}
                value={Number.isInteger(addSupportUserId) ? addSupportUserId : null}
                onChange={(v) => setAddSupportUserId(Number.isInteger(v) ? v : null)}
                style={{ width: 240 }}
                disabled={usersLoading || addSupportOptions.length === 0}
              />
              <Button
                size="sm"
                appearance="primary"
                onClick={submitAddSupport}
                loading={addSupporter.isPending}
                disabled={addSupporter.isPending || !Number.isInteger(addSupportUserId)}
              >
                Add
              </Button>
              <div style={{ fontSize: 12, color: '#777' }}>Click the red minus to remove a support.</div>
            </div>

            <Button size="sm" appearance="subtle" onClick={exitSupportEditMode}>
              Done
            </Button>
          </div>
        ) : null}

        {!viewAllActivities ? (
        <div ref={tableWrapRef} className="ppc-forecast-table-wrap">
        <Table
          className="ppc-forecast-table"
          data={rows}
          autoHeight
          bordered
          cellBordered
          loading={forecastLoading}
          rowKey={(row) => `${row.userId}`}
          rowClassName={(rowData) => (isTotalRow(rowData) ? 'ppc-forecast-total-row' : '')}
          rowHeight={66}
          headerHeight={38}
          style={{ width: '100%' }}
        >
          <Column width={160} fixed>
            <HeaderCell className="ppc-forecast-pinned-col">
              <div className="ppc-forecast-user-header">
                <span>Person</span>
                <span className="ppc-forecast-user-header__actions">
                  {!editingSupports ? (
                    canManageDisciplines ? (
                      <button
                        type="button"
                        className="ppc-forecast-user-header__icon"
                        title="Edit supports"
                        aria-label="Edit supports"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          enterSupportEditMode();
                        }}
                      >
                        <i className="bi bi-pencil" aria-hidden="true" />
                      </button>
                    ) : null
                  ) : (
                    <button
                      type="button"
                      className="ppc-forecast-user-header__icon"
                      style={{ opacity: 1, pointerEvents: 'auto' }}
                      title="Done"
                      aria-label="Done"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        exitSupportEditMode();
                      }}
                    >
                      <i className="bi bi-check-lg" aria-hidden="true" />
                    </button>
                  )}
                </span>
              </div>
            </HeaderCell>
            <Cell className="ppc-forecast-pinned-col">
              {(rowData) => {
                if (isTotalRow(rowData)) return <span style={{ fontWeight: 800 }}>Total</span>;
                const role = rowData?.role === 'lead' ? 'Lead' : 'Support';
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', textAlign: 'center' }}>
                    <div
                      style={{
                        position: 'relative',
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {editingSupports && rowData?.role !== 'lead' ? (
                        <button
                          type="button"
                          className="ppc-forecast-support-remove"
                          title="Remove support"
                          aria-label="Remove support"
                          disabled={setSupporters.isPending}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            requestRemoveSupport(rowData);
                          }}
                        >
                          <i className="bi bi-dash-circle-fill" aria-hidden="true" />
                        </button>
                      ) : null}
                      <span>{rowData?.userName || '—'}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#777' }}>{role}</div>
                  </div>
                );
              }}
            </Cell>
          </Column>

          <Column width={140} fixed>
            <HeaderCell className="ppc-forecast-pinned-col">Rate</HeaderCell>
            <Cell className="ppc-forecast-pinned-col">
              {(rowData) => {
                if (isTotalRow(rowData)) return <span style={{ fontWeight: 800 }}>—</span>;

                const isProjectForecast = Number.isInteger(Number(forecast?.projectId));
                const isMissingRateForMonth = (ms) => {
                  const r = getRateForMonth(rowData, ms);
                  if (r === null) return true;

                  // When forecasting within a project, a base (user) rate may be used as a fallback.
                  // We still want to warn if there is no project-specific effective-dated rate for the month.
                  if (isProjectForecast) {
                    const eff = getRateEffectiveDateForMonth(rowData, ms);
                    return eff === null;
                  }

                  return false;
                };

                const hasMissingRate = monthStartsForTable.some((ms) => isMissingRateForMonth(ms));
                const missingTooltipText = isProjectForecast
                  ? 'Missing project rate for one or more months (click to set rates)'
                  : 'Missing rate for one or more months (click to set rates)';

                const showMissingRateWarning = warningsVisible && hasMissingRate;

                const segments = buildRateSegmentsWithEffectiveDate(
                  monthStartsForTable,
                  (ms) => getRateForMonth(rowData, ms),
                  (ms) => getRateEffectiveDateForMonth(rowData, ms)
                );
                const displaySegments = segments.slice(0, 3);

                return (
                  <div
                    style={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: 2,
                      height: '100%',
                      paddingRight: 18,
                    }}
                  >
                    {showMissingRateWarning ? (
                      <div style={{ position: 'absolute', top: 2, right: 2 }}>
                        <Whisper placement="topEnd" trigger="hover" speaker={<Tooltip>{missingTooltipText}</Tooltip>}>
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const uid = Number(rowData?.userId);
                              if (Number.isInteger(uid)) {
                                navigate(`/users/${uid}#rates`);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                const uid = Number(rowData?.userId);
                                if (Number.isInteger(uid)) {
                                  navigate(`/users/${uid}#rates`);
                                }
                              }
                            }}
                            aria-label={missingTooltipText}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#fa8c16',
                              cursor: 'pointer',
                              lineHeight: 1,
                            }}
                          >
                            <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                          </span>
                        </Whisper>
                      </div>
                    ) : null}
                    {displaySegments.map((seg) => {
                      return (
                        <div key={`${String(seg.effectiveDate)}-${String(seg.rate)}`} style={{ lineHeight: '16px' }}>
                          <div style={{ fontWeight: 700 }}>{formatRate(seg.rate)}</div>
                          <div style={{ fontSize: 11, color: '#777' }}>Eff: {seg.effectiveDate || '—'}</div>
                        </div>
                      );
                    })}
                    {segments.length > displaySegments.length ? (
                      <div style={{ fontSize: 11, color: '#777' }}>…</div>
                    ) : null}
                  </div>
                );
              }}
            </Cell>
          </Column>

          {/* Zero-width columns holding the months just out of view on each side; see slideToStart. */}
          <Column width={0} align="right">
            <HeaderCell className="ppc-forecast-month-col ppc-forecast-month-ghost ppc-forecast-month-ghost--prev">
              {renderSlideInMonth(slideInMonths.prev, renderMonthHeader)}
            </HeaderCell>
            <Cell className="ppc-forecast-month-col ppc-forecast-month-ghost ppc-forecast-month-ghost--prev">
              {(rowData) => renderSlideInMonth(slideInMonths.prev, (ms) => renderMonthCell(rowData, ms))}
            </Cell>
          </Column>

          {monthStartsForTable.map((ms) => (
            <Column key={ms} width={170} flexGrow={1} align="right">
              <HeaderCell className="ppc-forecast-month-col">{renderMonthHeader(ms)}</HeaderCell>
              <Cell className="ppc-forecast-month-col">{(rowData) => renderMonthCell(rowData, ms)}</Cell>
            </Column>
          ))}

          <Column width={0} align="right">
            <HeaderCell className="ppc-forecast-month-col ppc-forecast-month-ghost ppc-forecast-month-ghost--next">
              {renderSlideInMonth(slideInMonths.next, renderMonthHeader)}
            </HeaderCell>
            <Cell className="ppc-forecast-month-col ppc-forecast-month-ghost ppc-forecast-month-ghost--next">
              {(rowData) => renderSlideInMonth(slideInMonths.next, (ms) => renderMonthCell(rowData, ms))}
            </Cell>
          </Column>

          <Column width={90} fixed="right" align="right">
            <HeaderCell className="ppc-forecast-pinned-col">Total (h)</HeaderCell>
            <Cell className="ppc-forecast-pinned-col">
              {(rowData) => {
                const getWindowServerTotal = (row) =>
                  monthStartsForTable.reduce((sum, ms) => sum + Number(row?.hours?.[ms] ?? 0), 0);

                const getWindowEffectiveTotal = (row) =>
                  monthStartsForTable.reduce((sum, ms) => sum + Number(getEffectiveHours(row, ms) ?? 0), 0);

                const getDisplayedTotal = (row) => {
                  if (isTotalRow(row)) return monthStartsForTable.reduce((sum, ms) => sum + Number(row?.hours?.[ms] ?? 0), 0);
                  return getWindowEffectiveTotal(row);
                };

                let total;
                if (!allTimeTotals) {
                  total = getDisplayedTotal(rowData);
                } else if (isTotalRow(rowData)) {
                  const baseOverall = Number(allTimeTotals?.totalHours ?? 0);
                  const deltaOverall = rows
                    .filter((r) => !isTotalRow(r))
                    .reduce((sum, r) => sum + (getWindowEffectiveTotal(r) - getWindowServerTotal(r)), 0);
                  total = baseOverall + deltaOverall;
                } else {
                  const uid = Number(rowData?.userId);
                  if (!allTimeTotalHoursByUserId.has(uid)) {
                    total = getDisplayedTotal(rowData);
                  } else {
                    const baseUser = Number(allTimeTotalHoursByUserId.get(uid) ?? 0);
                  const deltaUser = getWindowEffectiveTotal(rowData) - getWindowServerTotal(rowData);
                    total = baseUser + deltaUser;
                  }
                }

                return (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <span
                      style={{
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: isTotalRow(rowData) ? 800 : 700,
                      }}
                    >
                      {total.toFixed(2)}h
                    </span>
                  </div>
                );
              }}
            </Cell>
          </Column>
        </Table>
        </div>
        ) : null}

        <Modal open={discardConfirmOpen} onClose={closeDiscardConfirm} size="xs">
          <Modal.Header>
            <Modal.Title>Discard unsaved changes?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Message type="warning" showIcon>
              {discardConfirmMode === 'view-all'
                ? 'You have unsaved changes in one or more activity sections. Discard them and continue?'
                : 'You have unsaved hour changes. Discard them and continue?'}
            </Message>
          </Modal.Body>
          <Modal.Footer>
            <NsiButton onClick={closeDiscardConfirm}>
              Keep editing
            </NsiButton>
            <NsiButton variant="danger-solid" onClick={confirmDiscardWithModal}>
              Discard and continue
            </NsiButton>
          </Modal.Footer>
        </Modal>

        <Modal
          open={pastForecastOverrideModalOpen}
          onClose={() => setPastForecastOverrideModalOpen(false)}
          size="xs"
        >
          <Modal.Header>
            <Modal.Title>Override past forecast lock?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Message type="warning" showIcon>
              Are you sure you want to override past forecast locking for this session?
            </Message>
          </Modal.Body>
          <Modal.Footer>
            <NsiButton
              onClick={() => {
                setPastForecastOverrideModalOpen(false);
              }}
            >
              Close
            </NsiButton>
            <NsiButton
              variant="danger-solid"
              onClick={() => {
                setPastForecastOverrideEnabled(true);
                pastForecastUnlockAttemptCountRef.current = 0;
                setPastForecastOverrideModalOpen(false);
              }}
            >
              Override
            </NsiButton>
          </Modal.Footer>
        </Modal>

        {!viewAllActivities ? (
        <Modal
          open={removeSupportOpen}
          onClose={() => {
            setRemoveSupportOpen(false);
            setRemoveSupportUser(null);
          }}
          size="xs"
        >
          <Modal.Header>
            <Modal.Title>Remove support</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Message type="warning" showIcon>
              This will remove the user from the supports list.
            </Message>
            <div style={{ marginTop: 10, fontSize: 13 }}>
              Remove <b>{removeSupportUser?.userName || 'this user'}</b>?
            </div>
          </Modal.Body>
          <Modal.Footer>
            <NsiButton
              onClick={() => {
                setRemoveSupportOpen(false);
                setRemoveSupportUser(null);
              }}
              disabled={setSupporters.isPending}
            >
              Cancel
            </NsiButton>
            <NsiButton
              variant="danger-solid"
              onClick={confirmRemoveSupport}
              loading={setSupporters.isPending}
              disabled={setSupporters.isPending}
            >
              Remove
            </NsiButton>
          </Modal.Footer>
        </Modal>
        ) : null}

        {viewAllActivities ? (
        <Modal
          open={viewAllExportModalOpen}
          onClose={() => {
            if (!viewAllExporting) setViewAllExportModalOpen(false);
          }}
          size="xs"
        >
          <Modal.Header>
            <Modal.Title>Export forecast range</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <div style={{ fontSize: 12, color: '#666', fontWeight: 700, marginBottom: 6 }}>From month</div>
                <SelectPicker
                  block
                  searchable
                  cleanable={false}
                  data={viewAllExportMonthOptions}
                  value={viewAllExportStartMonth}
                  onChange={(value) => {
                    const v = typeof value === 'string' ? value : '';
                    if (isIsoDate(v)) setViewAllExportStartMonth(v);
                  }}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#666', fontWeight: 700, marginBottom: 6 }}>To month</div>
                <SelectPicker
                  block
                  searchable
                  cleanable={false}
                  data={viewAllExportMonthOptions}
                  value={viewAllExportEndMonth}
                  onChange={(value) => {
                    const v = typeof value === 'string' ? value : '';
                    if (isIsoDate(v)) setViewAllExportEndMonth(v);
                  }}
                />
              </div>

              {selectedViewAllExportMonths.length > 0 ? (
                <div style={{ fontSize: 12, color: '#666' }}>
                  Exporting {selectedViewAllExportMonths.length} month{selectedViewAllExportMonths.length === 1 ? '' : 's'} ({selectedViewAllExportRangeLabel})
                </div>
              ) : (
                <Message showIcon type="warning">
                  Choose a valid month range.
                </Message>
              )}

              {viewAllExportError ? (
                <Message showIcon type="error">
                  Failed to export forecast: {viewAllExportError}
                </Message>
              ) : null}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <NsiButton onClick={() => setViewAllExportModalOpen(false)} disabled={viewAllExporting}>
              Cancel
            </NsiButton>
            <NsiButton
              variant="primary"
              loading={viewAllExporting}
              disabled={selectedViewAllExportMonths.length === 0}
              onClick={handleViewAllExportConfirm}
            >
              Export
            </NsiButton>
          </Modal.Footer>
        </Modal>
        ) : null}

        {!viewAllActivities ? (
        <Modal
          open={singleExportModalOpen}
          onClose={() => {
            if (!singleExporting) setSingleExportModalOpen(false);
          }}
          size="xs"
        >
          <Modal.Header>
            <Modal.Title>Export forecast range</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <div style={{ fontSize: 12, color: '#666', fontWeight: 700, marginBottom: 6 }}>From month</div>
                <SelectPicker
                  block
                  searchable
                  cleanable={false}
                  data={viewAllExportMonthOptions}
                  value={singleExportStartMonth}
                  onChange={(value) => {
                    const v = typeof value === 'string' ? value : '';
                    if (isIsoDate(v)) setSingleExportStartMonth(v);
                  }}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#666', fontWeight: 700, marginBottom: 6 }}>To month</div>
                <SelectPicker
                  block
                  searchable
                  cleanable={false}
                  data={viewAllExportMonthOptions}
                  value={singleExportEndMonth}
                  onChange={(value) => {
                    const v = typeof value === 'string' ? value : '';
                    if (isIsoDate(v)) setSingleExportEndMonth(v);
                  }}
                />
              </div>

              {selectedSingleExportMonths.length > 0 ? (
                <div style={{ fontSize: 12, color: '#666' }}>
                  Exporting {selectedSingleExportMonths.length} month{selectedSingleExportMonths.length === 1 ? '' : 's'} ({selectedSingleExportRangeLabel})
                </div>
              ) : (
                <Message showIcon type="warning">
                  Choose a valid month range.
                </Message>
              )}

              {singleExportError ? (
                <Message showIcon type="error">
                  Failed to export forecast: {singleExportError}
                </Message>
              ) : null}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <NsiButton onClick={() => setSingleExportModalOpen(false)} disabled={singleExporting}>
              Cancel
            </NsiButton>
            <NsiButton
              variant="primary"
              loading={singleExporting}
              disabled={selectedSingleExportMonths.length === 0}
              onClick={handleSingleExportConfirm}
            >
              Export
            </NsiButton>
          </Modal.Footer>
        </Modal>
        ) : null}

        {!viewAllActivities ? (
        <div className="ppc-forecast-table-note">
          Enter hours per user per month. When two months in a row repeat the same hours, a lightning icon appears in the next empty month; hover over it to preview the hours it would fill, and click to accept (or press Ctrl+E in that row), then Save. Use Undo in the confirmation popup (or Ctrl+Z) to revert a fill before saving. Totals are shown in the last row. The right-most Total column shows all-time totals (with unsaved edits reflected for displayed months).
        </div>
        ) : null}

        <div className="ppc-forecast-summary-card">
          <div className="ppc-forecast-summary-top">
            <div className="ppc-forecast-summary-actions">
            <button
              type="button"
              className="ppc-actuals-btn ppc-forecast-btn--sm"
              onClick={() => setPrintMode(true)}
              disabled={printMode || viewAllActivities}
              title={viewAllActivities ? 'Switch to single activity view to print the forecast grid.' : undefined}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <i className="bi bi-printer" aria-hidden="true" />
                <span style={{ fontWeight: 700 }}>Print</span>
              </span>
            </button>
            <button
              type="button"
              className="ppc-actuals-btn ppc-forecast-btn--sm"
              onClick={viewAllActivities ? handleExportViewAllXlsx : handleExportSingleXlsx}
              disabled={viewAllActivities ? !canExportViewAll : !canExportSingle}
              title={
                viewAllActivities
                  ? (canExportViewAll ? 'Export all visible View All forecast data to Excel.' : viewAllExportDisabledReason)
                  : (canExportSingle ? 'Export the selected activity forecast data to Excel.' : singleExportDisabledReason)
              }
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <i className="bi bi-download" aria-hidden="true" />
                <span style={{ fontWeight: 700 }}>Export Excel</span>
              </span>
            </button>
            </div>

            <div className="ppc-forecast-summary-kpis">
            <div className="ppc-forecast-summary-kpi">
              <div className="ppc-forecast-summary-kpi__label">Allocated budget</div>
              <div className="ppc-forecast-summary-kpi__value">
                {formatCurrency(allocatedTaskBudget)}
              </div>
            </div>
            <div className="ppc-forecast-summary-kpi">
              <div className="ppc-forecast-summary-kpi__label">
                {viewAllActivities ? 'Full total hours' : 'Activity all-time hours'}
              </div>
              <div className="ppc-forecast-summary-kpi__value">
                {viewAllActivities ? Number(viewAllTotals.totalHours ?? 0).toFixed(2) : Number(allTimeTotals?.totalHours ?? 0).toFixed(2)}h
              </div>
            </div>
            <div className="ppc-forecast-summary-kpi">
              <div className="ppc-forecast-summary-kpi__label">
                {viewAllActivities ? 'Full total cost' : 'Activity all-time cost'}
              </div>
              <div className="ppc-forecast-summary-kpi__value">
                {viewAllActivities ? formatCurrency(viewAllTotals.totalCost) : formatCurrency(allTimeTotals?.totalCost)}
              </div>
            </div>
            </div>
          </div>

          {disciplineBudgetStatus ? (
            <div className="ppc-forecast-budget-status">
              <div className="ppc-forecast-budget-status__head">
                <div className="ppc-forecast-budget-status__summary">
                  <div className="ppc-forecast-budget-status__label">Budget status</div>
                  <div className="ppc-forecast-budget-status__description">
                    {`Discipline forecast ${formatCurrency(disciplineBudgetStatus.cost)} (${Number(disciplineBudgetStatus.hours).toFixed(2)} h) of ${formatCurrency(allocatedTaskBudget)} allocated`}
                  </div>
                </div>

                <div
                  className={`ppc-forecast-budget-status__delta ${disciplineBudgetStatus.isOver ? 'is-over' : 'is-under'}`}
                >
                  <span className="ppc-forecast-budget-status__delta-label">
                    {disciplineBudgetStatus.delta === 0
                      ? 'On discipline budget'
                      : disciplineBudgetStatus.isOver
                        ? 'Over discipline budget by'
                        : 'Under discipline budget by'}
                  </span>
                  {disciplineBudgetStatus.delta === 0 ? null : (
                    <>
                      <span className="ppc-forecast-budget-status__delta-value">
                        {formatCurrency(Math.abs(disciplineBudgetStatus.delta))}
                      </span>
                      <span className="ppc-forecast-budget-status__delta-pct">
                        {`(${disciplineBudgetStatus.percent.toFixed(2)}%)`}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div
                className="ppc-forecast-budget-status__bar"
                aria-hidden="true"
                title={`${Math.max(0, Number(disciplineBudgetUsagePercent ?? 0)).toFixed(2)}% of budget`}
              >
                <div
                  className="ppc-forecast-budget-status__bar-fill"
                  style={{ width: `${Math.min(100, Math.max(0, Number(disciplineBudgetUsagePercent ?? 0)))}%` }}
                />
                {Number(disciplineBudgetUsagePercent) > 100 ? (
                  <div
                    className="ppc-forecast-budget-status__overrun"
                    style={{ width: `${Math.min(100, Number(disciplineBudgetUsagePercent) - 100)}%` }}
                  />
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
        </div>
      </div>

      <div className="ppc-print-area" aria-hidden={!printMode}>
        <div className="ppc-print-header">
          <div style={{ fontSize: 18, fontWeight: 800 }}>Hour Forecast</div>
          <div style={{ marginTop: 2, color: '#444' }}>
            Activity: <span style={{ fontWeight: 700 }}>{selectedActivityLabel || '—'}</span>
          </div>
          <div style={{ marginTop: 2, color: '#444' }}>
            Task: <span style={{ fontWeight: 700 }}>{task?.name || task?.taskName || '—'}</span>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
            Range: {rangeLabel || '—'}{filterMonthsWithHours ? ' (filtered)' : ''} • Printed{' '}
            {new Date().toLocaleString()}
          </div>
        </div>

        <table className="ppc-print-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Person</th>
              <th style={{ textAlign: 'left' }}>Role</th>
              <th style={{ textAlign: 'right' }}>Rate</th>
              {monthStartsForTable.map((ms) => (
                <th key={ms} style={{ textAlign: 'right' }}>
                  {formatMonthLabel(ms)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const totalRow = isTotalRow(row);
              const roleLabel = totalRow ? '—' : row?.role === 'lead' ? 'Lead' : 'Support';

              return (
                <tr key={`${row?.userId}`} className={totalRow ? 'ppc-print-total-row' : undefined}>
                  <td style={{ textAlign: 'left', fontWeight: totalRow ? 800 : 600 }}>{row?.userName || '—'}</td>
                  <td style={{ textAlign: 'left' }}>{roleLabel}</td>
                  <td style={{ textAlign: 'right' }}>
                    {(() => {
                      if (totalRow) return '—';
                      const segs = buildRateSegments(monthStartsForTable, (ms) => getRateForMonth(row, ms));
                      const unique = Array.from(
                        new Set(
                          segs
                            .map((s) => Number(s.rate))
                            .filter((n) => Number.isFinite(n))
                            .map((n) => n.toFixed(2))
                        )
                      );
                      if (unique.length <= 1) return formatRate(row?.rate);
                      return unique.map((n) => formatRate(Number(n))).join(' / ');
                    })()}
                  </td>
                  {monthStartsForTable.map((ms) => {
                    const h = totalRow ? Number(row?.hours?.[ms] ?? 0) : Number(getEffectiveHours(row, ms) ?? 0);
                    const c =
                      totalRow
                        ? Number(row?.costs?.[ms] ?? 0)
                        : Number(getRateForMonth(row, ms) ?? 0) * Number(h ?? 0);
                    return (
                      <td key={ms} style={{ textAlign: 'right' }}>
                        <div style={{ fontVariantNumeric: 'tabular-nums' }}>{Number(h ?? 0).toFixed(2)}h</div>
                        <div style={{ fontSize: 11, color: '#555' }}>{formatCurrency(c)}</div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="ppc-print-summary">
          <div>
            Displayed hours:{' '}
            <span style={{ fontWeight: 800 }}>{Number(printDisplayedTotals?.displayedHours ?? 0).toFixed(2)}h</span>
          </div>
          <div>
            Displayed cost: <span style={{ fontWeight: 800 }}>{formatCurrency(printDisplayedTotals?.displayedCost)}</span>
          </div>
          <div>
            Total hours: <span style={{ fontWeight: 800 }}>{Number(allTimeTotals?.totalHours ?? 0).toFixed(2)}h</span>
          </div>
          <div>
            Total cost: <span style={{ fontWeight: 800 }}>{formatCurrency(allTimeTotals?.totalCost)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
